import AdminLayout from "@/components/layout-admin";
import { ApartmentLaundryItemsSection } from "@/components/apartment-laundry-items";
import { useApartment, useBuildings, useCreateChecklistItem, useCreatePhotoRequirement, useUpdateApartment, useAdminUsers, useQaCriteria, useUpdateQaCriteria, useClearUnitZones, useQaTemplates, useApplyQaTemplate } from "@/hooks/use-resources";
import { api } from "@shared/routes";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertChecklistItemSchema, insertPhotoRequirementSchema, INVENTORY_ZONES, INVENTORY_CONDITIONS } from "@shared/schema";
import type { ChecklistItem, PhotoRequirement } from "@shared/schema";
import { z } from "zod";
import { useState, useRef, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useRoute, Link } from "wouter";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { ArrowLeft, Plus, Copy, Trash2, Wifi, Clock, Key, Camera, ClipboardCheck, Pencil, RefreshCw, BedDouble, Upload, Star, ImageIcon, Loader2, X, Store, MapPin, DollarSign, Sparkles, Users, Package, AlertTriangle, CheckCircle2, XCircle, Wrench, Calendar, Link2, ClipboardEdit, Bell, AlertCircle, Shield, ChevronRight, Info, Receipt, Edit, TrendingDown, ExternalLink, TrendingUp, ScanLine, ChevronDown, ChevronUp, ArrowUp, ArrowDown, Wind } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { DeleteConfirmDialog } from "@/components/ui/delete-confirm-dialog";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";

type ApartmentPhoto = {
  id: number;
  apartmentId: number;
  organizationId: number;
  url: string;
  caption: string | null;
  sortOrder: number | null;
  isCover: boolean | null;
};

const checklistFormSchema = insertChecklistItemSchema.extend({
  textEn: z.string().min(1, "English text is required"),
  textEs: z.string().min(1, "Spanish text is required"),
  apartmentId: z.number(),
  isRequired: z.boolean().default(true),
});

const photoFormSchema = insertPhotoRequirementSchema.extend({
  labelEn: z.string().min(1, "English label is required"),
  labelEs: z.string().min(1, "Spanish label is required"),
  apartmentId: z.number(),
  minPhotos: z.coerce.number().min(1, "Minimum 1 photo"),
  isRequired: z.boolean().default(true),
});

type CleaningOverrideApartment = {
  id: number;
  bedrooms?: number | null;
  customCleaningFee?: string | null;
  customCleanerPay?: string | null;
};

type CommissionDefaults = {
  cleaningFee?: string | null;
  cleanerPay?: string | null;
  cleaningFeeStudio?: string | null;
  cleanerPayStudio?: string | null;
  cleaningFee2br?: string | null;
  cleanerPay2br?: string | null;
  cleaningFee3br?: string | null;
  cleanerPay3br?: string | null;
  cleaningFee4br?: string | null;
  cleanerPay4br?: string | null;
  cleaningFee5br?: string | null;
  cleanerPay5br?: string | null;
};

function getBedroomDefaults(bedrooms: number | null | undefined, settings: CommissionDefaults | null): { fee: string; pay: string } {
  const br = bedrooms ?? 1;
  if (br === 0) return { fee: settings?.cleaningFeeStudio || settings?.cleaningFee || "90.00", pay: settings?.cleanerPayStudio || settings?.cleanerPay || "60.00" };
  if (br === 2) return { fee: settings?.cleaningFee2br || settings?.cleaningFee || "140.00", pay: settings?.cleanerPay2br || settings?.cleanerPay || "95.00" };
  if (br === 3) return { fee: settings?.cleaningFee3br || settings?.cleaningFee || "170.00", pay: settings?.cleanerPay3br || settings?.cleanerPay || "115.00" };
  if (br === 4) return { fee: settings?.cleaningFee4br || settings?.cleaningFee || "200.00", pay: settings?.cleanerPay4br || settings?.cleanerPay || "140.00" };
  if (br >= 5) return { fee: settings?.cleaningFee5br || settings?.cleaningFee || "240.00", pay: settings?.cleanerPay5br || settings?.cleanerPay || "165.00" };
  return { fee: settings?.cleaningFee || "110.00", pay: settings?.cleanerPay || "75.00" };
}

function CleaningOverrideCard({ apartment, id }: { apartment: CleaningOverrideApartment; id: number }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [feeInput, setFeeInput] = useState("");
  const [payInput, setPayInput] = useState("");
  const [saving, setSaving] = useState(false);
  const updateApartment = useUpdateApartment();

  const { data: commissionSettings } = useQuery<CommissionDefaults>({
    queryKey: ["/api/admin/commission-settings"],
  });

  const brDefaults = getBedroomDefaults(apartment?.bedrooms, commissionSettings ?? null);

  useEffect(() => {
    if (apartment) {
      setFeeInput(apartment.customCleaningFee || "");
      setPayInput(apartment.customCleanerPay || "");
    }
  }, [apartment?.id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateApartment.mutateAsync({
        id,
        data: {
          customCleaningFee: feeInput || null,
          customCleanerPay: payInput || null,
        },
      });
      queryClient.invalidateQueries({ queryKey: ['/api/apartments', id] });
      toast({ title: "Cleaning override saved" });
    } catch {
      toast({ title: "Failed to save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await updateApartment.mutateAsync({ id, data: { customCleaningFee: null, customCleanerPay: null } });
      setFeeInput("");
      setPayInput("");
      queryClient.invalidateQueries({ queryKey: ['/api/apartments', id] });
      toast({ title: "Override cleared — using org-wide pricing" });
    } catch {
      toast({ title: "Failed to clear override", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const hasOverride = !!(apartment?.customCleaningFee && apartment?.customCleanerPay);
  const brLabel = apartment?.bedrooms === 0 ? "Studio" : apartment?.bedrooms ? `${apartment.bedrooms}BR` : "1BR";

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-primary" />
          <CardTitle className="text-lg">Cleaning Price Override</CardTitle>
          {hasOverride && (
            <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-xs" data-testid="badge-override-active-detail">
              Custom Price Active
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Set a custom cleaning fee and cleaner pay for this property. Both fields must be filled to activate the override. Leave both blank to use the org-wide pricing tiers (by bedroom count).
        </p>
        <div className="flex flex-wrap gap-4 items-end">
          <div className="space-y-1.5">
            <Label htmlFor="custom-cleaning-fee">Cleaning Fee (charged to guest)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
              <Input
                id="custom-cleaning-fee"
                type="number"
                step="0.01"
                min="0"
                placeholder={`Default for ${brLabel}: $${brDefaults.fee}`}
                value={feeInput}
                onChange={e => setFeeInput(e.target.value)}
                className="pl-6 w-52"
                data-testid="input-custom-cleaning-fee"
              />
            </div>
            <p className="text-xs text-muted-foreground">Default for {brLabel}: ${brDefaults.fee}</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="custom-cleaner-pay">Cleaner Pay</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
              <Input
                id="custom-cleaner-pay"
                type="number"
                step="0.01"
                min="0"
                placeholder={`Default for ${brLabel}: $${brDefaults.pay}`}
                value={payInput}
                onChange={e => setPayInput(e.target.value)}
                className="pl-6 w-52"
                data-testid="input-custom-cleaner-pay"
              />
            </div>
            <p className="text-xs text-muted-foreground">Default for {brLabel}: ${brDefaults.pay}</p>
          </div>
          <div className="flex gap-2 pb-5">
            <Button onClick={handleSave} disabled={saving} data-testid="button-save-cleaning-override">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Override
            </Button>
            {hasOverride && (
              <Button variant="ghost" onClick={handleClear} disabled={saving} data-testid="button-clear-cleaning-override">
                Clear Override
              </Button>
            )}
          </div>
        </div>
        {hasOverride && (
          <div className="text-xs text-amber-600 font-medium" data-testid="text-cleaning-override-active">
            Custom override active — org pricing tiers are bypassed for this property.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function UnitRulesCard({ id }: { id: number }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: effectiveRulesData, isLoading } = useQuery<{
    effective: any;
    pmRules: any;
    unitRules: any;
  }>({
    queryKey: ['/api/admin/apartments', id, 'effective-rules'],
    queryFn: async () => {
      const res = await apiRequest('GET', `/api/admin/apartments/${id}/effective-rules`);
      return res.json();
    },
    enabled: !!id,
  });

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<{
    checkoutTime: string;
    cleaningNotes: string;
    notifyOnCheckout: boolean | null;
    notifyOnCleaningComplete: boolean | null;
    requireFobReturn: boolean | null;
    defaultPriority: string;
    additionalNotes: string;
  }>({ checkoutTime: "", cleaningNotes: "", notifyOnCheckout: null, notifyOnCleaningComplete: null, requireFobReturn: null, defaultPriority: "", additionalNotes: "" });

  useEffect(() => {
    if (effectiveRulesData?.unitRules) {
      const u = effectiveRulesData.unitRules;
      setForm({
        checkoutTime: u.checkoutTime ?? "",
        cleaningNotes: u.cleaningNotes ?? "",
        notifyOnCheckout: u.notifyOnCheckout !== undefined ? u.notifyOnCheckout : null,
        notifyOnCleaningComplete: u.notifyOnCleaningComplete !== undefined ? u.notifyOnCleaningComplete : null,
        requireFobReturn: u.requireFobReturn !== undefined ? u.requireFobReturn : null,
        defaultPriority: u.defaultPriority ?? "",
        additionalNotes: u.additionalNotes ?? "",
      });
    }
  }, [effectiveRulesData?.unitRules]);

  const saveRulesMutation = useMutation({
    mutationFn: async (rules: any) => {
      const res = await apiRequest('PUT', `/api/admin/apartments/${id}/unit-rules`, rules);
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/apartments', id, 'effective-rules'] });
      const jobMsg = data?.updatedJobs > 0 ? ` — ${data.updatedJobs} pending job${data.updatedJobs === 1 ? '' : 's'} updated` : '';
      toast({ title: `Unit rules saved${jobMsg}` });
      setEditing(false);
    },
    onError: () => toast({ title: "Failed to save rules", variant: "destructive" }),
  });

  const clearOverrideMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('PUT', `/api/admin/apartments/${id}/unit-rules`, {});
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/apartments', id, 'effective-rules'] });
      const jobMsg = data?.updatedJobs > 0 ? ` — ${data.updatedJobs} pending job${data.updatedJobs === 1 ? '' : 's'} updated` : '';
      toast({ title: `Unit overrides cleared — using PM defaults${jobMsg}` });
    },
    onError: () => toast({ title: "Failed to clear overrides", variant: "destructive" }),
  });

  const handleSave = () => {
    const rules: any = {};
    if (form.checkoutTime) rules.checkoutTime = form.checkoutTime;
    if (form.cleaningNotes) rules.cleaningNotes = form.cleaningNotes;
    if (form.notifyOnCheckout !== null) rules.notifyOnCheckout = form.notifyOnCheckout;
    if (form.notifyOnCleaningComplete !== null) rules.notifyOnCleaningComplete = form.notifyOnCleaningComplete;
    if (form.requireFobReturn !== null) rules.requireFobReturn = form.requireFobReturn;
    if (form.defaultPriority) rules.defaultPriority = form.defaultPriority;
    if (form.additionalNotes) rules.additionalNotes = form.additionalNotes;
    saveRulesMutation.mutate(rules);
  };

  const pm = effectiveRulesData?.pmRules ?? {};
  const unit = effectiveRulesData?.unitRules ?? {};
  const eff = effectiveRulesData?.effective ?? {};
  const hasUnitOverrides = Object.keys(unit).length > 0;
  const hasPmRules = Object.keys(pm).length > 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <ClipboardEdit className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">Rules & Settings</CardTitle>
            {hasUnitOverrides && (
              <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-xs" data-testid="badge-unit-overrides-active">
                Unit Overrides Active
              </Badge>
            )}
          </div>
          <div className="flex gap-2">
            {hasUnitOverrides && !editing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => clearOverrideMutation.mutate()}
                disabled={clearOverrideMutation.isPending}
                data-testid="button-clear-unit-rules"
              >
                Clear Overrides
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditing(!editing)}
              data-testid="button-edit-unit-rules"
            >
              <Pencil className="w-4 h-4 mr-1" />
              {editing ? "Cancel" : "Override"}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : !editing ? (
          <>
            {!hasPmRules && !hasUnitOverrides && (
              <div className="text-sm text-muted-foreground py-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                No PM-level rules configured yet. Add rules to the Property Manager to see defaults here.
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {eff.checkoutTime !== undefined && (
                <div className="flex items-start gap-2 p-3 rounded-lg border bg-muted/30">
                  <Clock className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Checkout Time</p>
                    <p className="text-sm font-medium" data-testid="text-effective-checkout-time">{eff.checkoutTime}</p>
                    {unit.checkoutTime !== undefined && (
                      <Badge variant="outline" className="text-xs mt-1">Unit override</Badge>
                    )}
                  </div>
                </div>
              )}
              {eff.defaultPriority !== undefined && (
                <div className="flex items-start gap-2 p-3 rounded-lg border bg-muted/30">
                  <AlertCircle className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Default Priority</p>
                    <p className="text-sm font-medium capitalize" data-testid="text-effective-priority">{eff.defaultPriority}</p>
                    {unit.defaultPriority !== undefined && (
                      <Badge variant="outline" className="text-xs mt-1">Unit override</Badge>
                    )}
                  </div>
                </div>
              )}
              {eff.requireFobReturn !== undefined && (
                <div className="flex items-start gap-2 p-3 rounded-lg border bg-muted/30">
                  <Key className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Require FOB Return</p>
                    <p className="text-sm font-medium" data-testid="text-effective-fob-return">{eff.requireFobReturn ? "Yes" : "No"}</p>
                    {unit.requireFobReturn !== undefined && (
                      <Badge variant="outline" className="text-xs mt-1">Unit override</Badge>
                    )}
                  </div>
                </div>
              )}
              {eff.notifyOnCheckout !== undefined && (
                <div className="flex items-start gap-2 p-3 rounded-lg border bg-muted/30">
                  <Bell className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Notify on Checkout</p>
                    <p className="text-sm font-medium" data-testid="text-effective-notify-checkout">{eff.notifyOnCheckout ? "Yes" : "No"}</p>
                    {unit.notifyOnCheckout !== undefined && (
                      <Badge variant="outline" className="text-xs mt-1">Unit override</Badge>
                    )}
                  </div>
                </div>
              )}
              {eff.notifyOnCleaningComplete !== undefined && (
                <div className="flex items-start gap-2 p-3 rounded-lg border bg-muted/30">
                  <Bell className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Notify on Cleaning Done</p>
                    <p className="text-sm font-medium" data-testid="text-effective-notify-cleaning">{eff.notifyOnCleaningComplete ? "Yes" : "No"}</p>
                    {unit.notifyOnCleaningComplete !== undefined && (
                      <Badge variant="outline" className="text-xs mt-1">Unit override</Badge>
                    )}
                  </div>
                </div>
              )}
            </div>
            {eff.cleaningNotes && (
              <div className="p-3 rounded-lg border bg-muted/30">
                <p className="text-xs text-muted-foreground mb-1">Cleaning Notes</p>
                <p className="text-sm" data-testid="text-effective-cleaning-notes">{eff.cleaningNotes}</p>
                {unit.cleaningNotes !== undefined && (
                  <Badge variant="outline" className="text-xs mt-1">Unit override</Badge>
                )}
              </div>
            )}
            {eff.additionalNotes && (
              <div className="p-3 rounded-lg border bg-muted/30">
                <p className="text-xs text-muted-foreground mb-1">Additional Notes</p>
                <p className="text-sm" data-testid="text-effective-additional-notes">{eff.additionalNotes}</p>
                {unit.additionalNotes !== undefined && (
                  <Badge variant="outline" className="text-xs mt-1">Unit override</Badge>
                )}
              </div>
            )}
            {(hasPmRules || hasUnitOverrides) && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 pt-1">
                <Shield className="w-3 h-3" />
                Unit overrides take priority over PM-level defaults. Click "Override" to customize rules for this unit.
              </p>
            )}
          </>
        ) : (
          <div className="space-y-4">
            <div className="rounded-md border bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-200">
              Unit-level settings override PM defaults. Leave fields blank to inherit from the PM.
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1.5 text-sm font-medium">
                <Clock className="w-4 h-4 text-muted-foreground" />
                Checkout Time Override
                {pm.defaultCheckoutTime && <span className="text-xs text-muted-foreground font-normal">(PM default: {pm.defaultCheckoutTime})</span>}
              </Label>
              {(() => {
                const presetTimes = ["09:00","09:30","10:00","10:30","11:00","11:30","12:00","12:30","13:00","14:00","15:00"];
                const legacyValue = form.checkoutTime && !presetTimes.includes(form.checkoutTime) ? form.checkoutTime : null;
                return (
                  <Select
                    value={form.checkoutTime || "inherit"}
                    onValueChange={v => setForm({ ...form, checkoutTime: v === "inherit" ? "" : v })}
                  >
                    <SelectTrigger data-testid="select-unit-checkout-time">
                      <SelectValue placeholder="Inherit from PM..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inherit">Inherit from PM</SelectItem>
                      {legacyValue && <SelectItem value={legacyValue}>{legacyValue} (current)</SelectItem>}
                      <SelectItem value="09:00">9:00 AM</SelectItem>
                      <SelectItem value="09:30">9:30 AM</SelectItem>
                      <SelectItem value="10:00">10:00 AM</SelectItem>
                      <SelectItem value="10:30">10:30 AM</SelectItem>
                      <SelectItem value="11:00">11:00 AM</SelectItem>
                      <SelectItem value="11:30">11:30 AM</SelectItem>
                      <SelectItem value="12:00">12:00 PM (Noon)</SelectItem>
                      <SelectItem value="12:30">12:30 PM</SelectItem>
                      <SelectItem value="13:00">1:00 PM</SelectItem>
                      <SelectItem value="14:00">2:00 PM</SelectItem>
                      <SelectItem value="15:00">3:00 PM</SelectItem>
                    </SelectContent>
                  </Select>
                );
              })()}
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1.5 text-sm font-medium">
                <ClipboardEdit className="w-4 h-4 text-muted-foreground" />
                Cleaning Notes Override
                {pm.cleaningNotes && <span className="text-xs text-muted-foreground font-normal">(PM default set)</span>}
              </Label>
              <Textarea
                value={form.cleaningNotes}
                onChange={e => setForm({ ...form, cleaningNotes: e.target.value })}
                placeholder={pm.cleaningNotes || "Override cleaning notes for this unit..."}
                rows={3}
                data-testid="input-unit-cleaning-notes"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1.5 text-sm font-medium">
                <AlertCircle className="w-4 h-4 text-muted-foreground" />
                Priority Override
                {pm.defaultPriority && <span className="text-xs text-muted-foreground font-normal">(PM default: {pm.defaultPriority})</span>}
              </Label>
              <Select
                value={form.defaultPriority || "inherit"}
                onValueChange={v => setForm({ ...form, defaultPriority: v === "inherit" ? "" : v })}
              >
                <SelectTrigger data-testid="select-unit-priority">
                  <SelectValue placeholder="Inherit from PM..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inherit">Inherit from PM</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="border rounded-lg p-4 space-y-3">
              <p className="text-sm font-medium flex items-center gap-1.5">
                <Bell className="w-4 h-4 text-muted-foreground" />
                Notification & Access Overrides
              </p>
              <p className="text-xs text-muted-foreground -mt-1">Select "Inherit" to use the PM-level setting for each field.</p>
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <Label className="text-sm font-normal">Notify on Checkout</Label>
                    {pm.notifyOnCheckout !== undefined && (
                      <p className="text-xs text-muted-foreground">PM default: {pm.notifyOnCheckout ? "Yes" : "No"}</p>
                    )}
                  </div>
                  <Select
                    value={form.notifyOnCheckout === null ? "inherit" : form.notifyOnCheckout ? "true" : "false"}
                    onValueChange={v => setForm({ ...form, notifyOnCheckout: v === "inherit" ? null : v === "true" })}
                  >
                    <SelectTrigger className="w-32 shrink-0" data-testid="select-unit-notify-checkout">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inherit">Inherit PM</SelectItem>
                      <SelectItem value="true">Yes</SelectItem>
                      <SelectItem value="false">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <Label className="text-sm font-normal">Notify on Cleaning Done</Label>
                    {pm.notifyOnCleaningComplete !== undefined && (
                      <p className="text-xs text-muted-foreground">PM default: {pm.notifyOnCleaningComplete ? "Yes" : "No"}</p>
                    )}
                  </div>
                  <Select
                    value={form.notifyOnCleaningComplete === null ? "inherit" : form.notifyOnCleaningComplete ? "true" : "false"}
                    onValueChange={v => setForm({ ...form, notifyOnCleaningComplete: v === "inherit" ? null : v === "true" })}
                  >
                    <SelectTrigger className="w-32 shrink-0" data-testid="select-unit-notify-cleaning">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inherit">Inherit PM</SelectItem>
                      <SelectItem value="true">Yes</SelectItem>
                      <SelectItem value="false">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <Label className="text-sm font-normal">Require FOB Return</Label>
                    {pm.requireFobReturn !== undefined && (
                      <p className="text-xs text-muted-foreground">PM default: {pm.requireFobReturn ? "Yes" : "No"}</p>
                    )}
                  </div>
                  <Select
                    value={form.requireFobReturn === null ? "inherit" : form.requireFobReturn ? "true" : "false"}
                    onValueChange={v => setForm({ ...form, requireFobReturn: v === "inherit" ? null : v === "true" })}
                  >
                    <SelectTrigger className="w-32 shrink-0" data-testid="select-unit-fob-return">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inherit">Inherit PM</SelectItem>
                      <SelectItem value="true">Yes</SelectItem>
                      <SelectItem value="false">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Additional Notes Override</Label>
              <Textarea
                value={form.additionalNotes}
                onChange={e => setForm({ ...form, additionalNotes: e.target.value })}
                placeholder={pm.additionalNotes || "Override notes for this specific unit..."}
                rows={2}
                data-testid="input-unit-additional-notes"
              />
            </div>

            <div className="flex gap-2">
              <Button className="flex-1" onClick={handleSave} disabled={saveRulesMutation.isPending} data-testid="button-save-unit-rules">
                {saveRulesMutation.isPending ? "Saving..." : "Save Unit Overrides"}
              </Button>
              <Button variant="outline" onClick={() => setEditing(false)} data-testid="button-cancel-unit-rules">Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

type ActivityPanelRecord =
  | { type: "cleaning"; record: any; linkedFinancial?: any }
  | { type: "maintenance"; record: any }
  | { type: "financial"; record: any }
  | { type: "reservation"; record: any }
  | null;

function ActivityStatCard({ icon, label, value, sub, color }: { icon: React.ReactNode; label: string; value: string; sub?: string; color?: string }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center gap-2 mb-1">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${color || "bg-primary/10"}`}>
            {icon}
          </div>
          <span className="text-xs text-muted-foreground font-medium">{label}</span>
        </div>
        <p className="text-xl font-bold tabular-nums">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <span className="text-sm text-muted-foreground shrink-0">{label}</span>
      <span className="text-sm font-medium text-right">{value || <span className="text-muted-foreground">—</span>}</span>
    </div>
  );
}

function ApartmentActivitySection({ apartmentId }: { apartmentId: number }) {
  const [actTab, setActTab] = useState<"cleaning" | "maintenance" | "financials" | "reservations">("cleaning");
  const [selected, setSelected] = useState<ActivityPanelRecord>(null);

  const { data, isLoading } = useQuery<{
    cleaningJobs: any[];
    maintenanceJobs: any[];
    financials: any[];
    reservations: any[];
  }>({
    queryKey: ["/api/admin/apartments", apartmentId, "activity"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/admin/apartments/${apartmentId}/activity`);
      return res.json();
    },
  });

  const fmt = (n: number) =>
    n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 });

  const fmtDate = (d: string | null | undefined) =>
    d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      completed: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
      pending: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
      in_progress: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
      confirmed: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
      paid: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
      cancelled: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
    };
    return (
      <Badge className={`text-xs capitalize ${map[status] || "bg-muted text-muted-foreground"}`}>
        {status?.replace(/_/g, " ") || "—"}
      </Badge>
    );
  };

  const tabs = [
    { id: "cleaning", label: "Cleaning", icon: <CheckCircle2 className="w-3.5 h-3.5" />, count: data?.cleaningJobs.length },
    { id: "maintenance", label: "Maintenance", icon: <Wrench className="w-3.5 h-3.5" />, count: data?.maintenanceJobs.length },
    { id: "financials", label: "Financials", icon: <DollarSign className="w-3.5 h-3.5" />, count: data?.financials.length },
    { id: "reservations", label: "Reservations", icon: <Calendar className="w-3.5 h-3.5" />, count: data?.reservations.length },
  ] as const;

  const totalRevenue = (data?.financials ?? []).reduce((sum: number, f: any) => sum + parseFloat(f.totalAmount || "0"), 0);
  const cleaningCompleted = (data?.cleaningJobs ?? []).filter((j: any) => j.status === "completed").length;

  return (
    <div className="space-y-4">
      {/* Stats Row */}
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <ActivityStatCard
            icon={<Calendar className="w-4 h-4 text-primary" />}
            label="Total Reservations"
            value={String(data?.reservations.length ?? 0)}
            sub="all time"
            color="bg-primary/10"
          />
          <ActivityStatCard
            icon={<CheckCircle2 className="w-4 h-4 text-green-600" />}
            label="Cleans Completed"
            value={String(cleaningCompleted)}
            sub={`of ${data?.cleaningJobs.length ?? 0} total`}
            color="bg-green-100 dark:bg-green-900/30"
          />
          <ActivityStatCard
            icon={<TrendingUp className="w-4 h-4 text-blue-600" />}
            label="Total Revenue"
            value={fmt(totalRevenue)}
            sub="from financials"
            color="bg-blue-100 dark:bg-blue-900/30"
          />
          <ActivityStatCard
            icon={<Wrench className="w-4 h-4 text-amber-600" />}
            label="Maintenance Jobs"
            value={String(data?.maintenanceJobs.length ?? 0)}
            sub="all time"
            color="bg-amber-100 dark:bg-amber-900/30"
          />
        </div>
      )}

      {/* Tab switcher */}
      <div className="flex gap-2 flex-wrap">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${actTab === t.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}
            data-testid={`btn-activity-${t.id}`}
          >
            {t.icon} {t.label}
            {t.count != null && (
              <span className={`ml-1 text-xs rounded-full px-1.5 py-0.5 ${actTab === t.id ? "bg-white/20" : "bg-background"}`}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}</div>
      ) : (
        <>
          {actTab === "cleaning" && (
            <Card>
              <CardContent className="p-0">
                {!data?.cleaningJobs.length ? (
                  <div className="flex flex-col items-center justify-center py-14 gap-2 text-muted-foreground">
                    <CheckCircle2 className="w-8 h-8 opacity-30" />
                    <p className="text-sm">No cleaning jobs yet.</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {data.cleaningJobs.map((j: any) => (
                      <button
                        key={j.id}
                        onClick={() => setSelected({ type: "cleaning", record: j, linkedFinancial: data?.financials.find((f: any) => f.cleaningJobId === j.id) })}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors text-left group"
                        data-testid={`row-cleaning-${j.id}`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center shrink-0">
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">
                              {j.cleanerName || <span className="text-muted-foreground italic">Unassigned</span>}
                            </span>
                            {j.secondCleanerName && (
                              <span className="text-xs text-muted-foreground">+ {j.secondCleanerName}</span>
                            )}
                            {statusBadge(j.status)}
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="text-xs text-muted-foreground capitalize">
                              {j.jobType?.replace(/_/g, " ") || "—"}
                            </span>
                            {j.scheduledDate && (
                              <span className="text-xs text-muted-foreground">{fmtDate(j.scheduledDate)}</span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {actTab === "maintenance" && (
            <Card>
              <CardContent className="p-0">
                {!data?.maintenanceJobs.length ? (
                  <div className="flex flex-col items-center justify-center py-14 gap-2 text-muted-foreground">
                    <Wrench className="w-8 h-8 opacity-30" />
                    <p className="text-sm">No maintenance jobs yet.</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {data.maintenanceJobs.map((j: any) => (
                      <button
                        key={j.id}
                        onClick={() => setSelected({ type: "maintenance", record: j })}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors text-left group"
                        data-testid={`row-maintenance-${j.id}`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
                          <Wrench className="w-4 h-4 text-amber-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm truncate max-w-[220px]">{j.description || "—"}</span>
                            {statusBadge(j.status)}
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="text-xs text-muted-foreground capitalize">
                              {j.category?.replace(/_/g, " ") || "—"}
                            </span>
                            {j.createdAt && (
                              <span className="text-xs text-muted-foreground">{fmtDate(j.createdAt)}</span>
                            )}
                            {j.billableCost && (
                              <span className="text-xs font-medium text-amber-600">{fmt(parseFloat(j.billableCost))}</span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {actTab === "financials" && (
            <Card>
              <CardContent className="p-0">
                {!data?.financials.length ? (
                  <div className="flex flex-col items-center justify-center py-14 gap-2 text-muted-foreground">
                    <DollarSign className="w-8 h-8 opacity-30" />
                    <p className="text-sm">No financial records yet.</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {data.financials.map((f: any) => (
                      <button
                        key={f.id}
                        onClick={() => setSelected({ type: "financial", record: f })}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors text-left group"
                        data-testid={`row-fin-${f.id}`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                          <DollarSign className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm truncate max-w-[200px]">{f.description || "—"}</span>
                            <Badge variant="outline" className="text-xs capitalize">{(f.type || "").replace(/_/g, " ")}</Badge>
                            {statusBadge(f.status)}
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="text-xs font-semibold tabular-nums">{fmt(parseFloat(f.totalAmount || "0"))}</span>
                            {f.createdAt && (
                              <span className="text-xs text-muted-foreground">{fmtDate(f.createdAt)}</span>
                            )}
                            {f.paymentMethod && (
                              <span className="text-xs text-muted-foreground capitalize">{f.paymentMethod.replace(/_/g, " ")}</span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {actTab === "reservations" && (
            <Card>
              <CardContent className="p-0">
                {!data?.reservations.length ? (
                  <div className="flex flex-col items-center justify-center py-14 gap-2 text-muted-foreground">
                    <Calendar className="w-8 h-8 opacity-30" />
                    <p className="text-sm">No reservations yet.</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {data.reservations.map((r: any) => (
                      <button
                        key={r.id}
                        onClick={() => setSelected({ type: "reservation", record: r })}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors text-left group"
                        data-testid={`row-res-${r.id}`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <Calendar className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">{r.guestName || "—"}</span>
                            {statusBadge(r.status)}
                            {r.source && (
                              <Badge variant="outline" className="text-xs capitalize">{r.source.replace(/_/g, " ")}</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            {r.checkInDate && r.checkOutDate && (
                              <span className="text-xs text-muted-foreground">
                                {fmtDate(r.checkInDate)} → {fmtDate(r.checkOutDate)}
                              </span>
                            )}
                            {r.paidAmount && (
                              <span className="text-xs font-medium tabular-nums">{fmt(parseFloat(r.paidAmount))}</span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Detail Slide Panel */}
      <Sheet open={!!selected} onOpenChange={open => { if (!open) setSelected(null); }}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          {selected?.type === "cleaning" && <CleaningDetailPanel record={selected.record} linkedFinancial={selected.linkedFinancial} statusBadge={statusBadge} fmtDate={fmtDate} fmt={fmt} />}
          {selected?.type === "maintenance" && <MaintenanceDetailPanel record={selected.record} statusBadge={statusBadge} fmt={fmt} fmtDate={fmtDate} />}
          {selected?.type === "financial" && <FinancialDetailPanel record={selected.record} statusBadge={statusBadge} fmt={fmt} fmtDate={fmtDate} />}
          {selected?.type === "reservation" && <ReservationDetailPanel record={selected.record} statusBadge={statusBadge} fmt={fmt} fmtDate={fmtDate} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function CleaningDetailPanel({ record: j, linkedFinancial, statusBadge, fmtDate, fmt }: { record: any; linkedFinancial?: any; statusBadge: (s: string) => React.ReactNode; fmtDate: (d: any) => string; fmt: (n: number) => string }) {
  const { data: supplyMovements = [] } = useQuery<any[]>({
    queryKey: ["/api/admin/inventory/movements", { cleaningJobId: j.id }],
    queryFn: () => fetch(`/api/admin/inventory/movements?cleaningJobId=${j.id}`).then(r => r.json()),
    enabled: !!j.id,
  });
  const consumptionMovements = supplyMovements.filter((m: any) => m.type === 'consumption');

  return (
    <>
      <SheetHeader className="mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <SheetTitle>Cleaning Job #{j.id}</SheetTitle>
            <p className="text-sm text-muted-foreground capitalize">{j.jobType?.replace(/_/g, " ") || "checkout"}</p>
          </div>
        </div>
      </SheetHeader>
      <Separator />
      <div className="mt-4 divide-y">
        <DetailRow label="Status" value={statusBadge(j.status)} />
        <DetailRow label="Priority" value={j.priority ? <span className="capitalize font-medium">{j.priority}</span> : null} />
        <DetailRow label="Job Type" value={j.jobType ? <span className="capitalize">{j.jobType.replace(/_/g, " ")}</span> : null} />
        <DetailRow label="Assigned Cleaner" value={j.cleanerName || <span className="italic text-muted-foreground">Unassigned</span>} />
        <DetailRow label="Second Cleaner" value={j.secondCleanerName || null} />
        <DetailRow label="Scheduled Date" value={fmtDate(j.scheduledDate)} />
        <DetailRow label="Deadline" value={fmtDate(j.deadlineAt)} />
        <DetailRow label="Completed At" value={fmtDate(j.completedAt)} />
        <DetailRow label="Created" value={fmtDate(j.createdAt)} />
        {j.customFeeOverride && (
          <DetailRow label="Custom Fee" value={fmt(parseFloat(j.customFeeOverride))} />
        )}
        {j.customCleanerPayOverride && (
          <DetailRow label="Cleaner Pay Override" value={fmt(parseFloat(j.customCleanerPayOverride))} />
        )}
        {j.reservationId && (
          <DetailRow label="Linked Reservation" value={<span className="text-primary">#{j.reservationId}</span>} />
        )}
        {linkedFinancial && (
          <DetailRow label="Financial Status" value={statusBadge(linkedFinancial.status)} />
        )}
        {linkedFinancial?.totalAmount && (
          <DetailRow label="Financial Amount" value={fmt(parseFloat(linkedFinancial.totalAmount))} />
        )}
        {j.notes && (
          <div className="py-2">
            <p className="text-sm text-muted-foreground mb-1">Notes</p>
            <p className="text-sm leading-relaxed">{j.notes}</p>
          </div>
        )}
      </div>

      {consumptionMovements.length > 0 && (
        <div className="mt-5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
            <Package className="h-3.5 w-3.5" /> Supplies Used
          </p>
          <div className="rounded-lg border overflow-hidden">
            {consumptionMovements.map((m: any, idx: number) => {
              const qty = Math.abs(parseFloat(m.quantity));
              const isPm = m.ownerType === 'pm_client';
              return (
                <div key={m.id} className={`flex items-center justify-between px-3 py-2 text-xs ${idx > 0 ? 'border-t' : ''} hover:bg-muted/30`} data-testid={`supply-movement-${m.id}`}>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{m.notes?.split('(')[0]?.trim() || `Item #${m.supplyCatalogId}`}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`font-bold ${isPm ? 'text-blue-600' : 'text-amber-700'}`}>−{qty}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isPm ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'}`}>
                      {isPm ? (m.ownerName || 'PM') : 'Org'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6">
        <Link href="/admin/cleaning-jobs">
          <Button variant="outline" className="w-full gap-2" data-testid="btn-view-cleaning-full">
            <ExternalLink className="w-4 h-4" /> View Full Cleaning Records
          </Button>
        </Link>
      </div>
    </>
  );
}

function MaintenanceDetailPanel({ record: j, statusBadge, fmt, fmtDate }: { record: any; statusBadge: (s: string) => React.ReactNode; fmt: (n: number) => string; fmtDate: (d: any) => string }) {
  return (
    <>
      <SheetHeader className="mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
            <Wrench className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <SheetTitle className="leading-snug">{j.title || `Maintenance #${j.id}`}</SheetTitle>
            <p className="text-sm text-muted-foreground capitalize">{j.priority || "medium"} priority</p>
          </div>
        </div>
      </SheetHeader>
      <Separator />
      <div className="mt-4 divide-y">
        <DetailRow label="Status" value={statusBadge(j.status)} />
        <DetailRow label="Priority" value={j.priority ? <span className="capitalize font-medium">{j.priority}</span> : null} />
        <DetailRow label="Category" value={j.category ? <span className="capitalize">{j.category.replace(/_/g, " ")}</span> : null} />
        <DetailRow label="Assigned Technician" value={j.assignedToUserId ? `User #${j.assignedToUserId}` : <span className="italic text-muted-foreground">Unassigned</span>} />
        <DetailRow label="Billable" value={j.isBillable ? <Badge className="bg-amber-100 text-amber-700 text-xs">Billable</Badge> : <span className="text-muted-foreground text-xs">Not billable</span>} />
        {j.costAmount && parseFloat(j.costAmount) > 0 && (
          <DetailRow label="Cost Amount" value={<span className="font-semibold">{fmt(parseFloat(j.costAmount))}</span>} />
        )}
        {j.costDescription && (
          <DetailRow label="Cost Description" value={j.costDescription} />
        )}
        <DetailRow label="Scheduled Date" value={fmtDate(j.scheduledDate)} />
        <DetailRow label="Completed At" value={fmtDate(j.completedAt)} />
        <DetailRow label="Approved At" value={fmtDate(j.approvedAt)} />
        <DetailRow label="Created" value={fmtDate(j.createdAt)} />
        {j.rejectionReason && (
          <DetailRow label="Rejection Reason" value={<span className="text-red-600">{j.rejectionReason}</span>} />
        )}
        {j.description && (
          <div className="py-2">
            <p className="text-sm text-muted-foreground mb-1">Description</p>
            <p className="text-sm leading-relaxed">{j.description}</p>
          </div>
        )}
      </div>
      <div className="mt-6">
        <Link href="/admin/maintenance">
          <Button variant="outline" className="w-full gap-2" data-testid="btn-view-maintenance-full">
            <ExternalLink className="w-4 h-4" /> View Full Maintenance Records
          </Button>
        </Link>
      </div>
    </>
  );
}

function FinancialDetailPanel({ record: f, statusBadge, fmt, fmtDate }: { record: any; statusBadge: (s: string) => React.ReactNode; fmt: (n: number) => string; fmtDate: (d: any) => string }) {
  return (
    <>
      <SheetHeader className="mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
            <DollarSign className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <SheetTitle>Financial Record #{f.id}</SheetTitle>
            <p className="text-sm text-muted-foreground capitalize">{(f.type || "").replace(/_/g, " ") || "—"}</p>
          </div>
        </div>
      </SheetHeader>
      <Separator />
      <div className="mt-4 divide-y">
        <DetailRow label="Status" value={statusBadge(f.status)} />
        <DetailRow label="Type" value={f.type ? <Badge variant="outline" className="text-xs capitalize">{f.type.replace(/_/g, " ")}</Badge> : null} />
        <DetailRow label="Total Amount" value={<span className="font-semibold text-base">{fmt(parseFloat(f.totalAmount || "0"))}</span>} />
        <DetailRow label="Admin Share" value={f.adminAmount ? fmt(parseFloat(f.adminAmount)) : null} />
        <DetailRow label="Cleaner Share" value={f.cleanerAmount && parseFloat(f.cleanerAmount) > 0 ? fmt(parseFloat(f.cleanerAmount)) : null} />
        <DetailRow label="Partner Share" value={f.partnerAmount && parseFloat(f.partnerAmount) > 0 ? fmt(parseFloat(f.partnerAmount)) : null} />
        <DetailRow label="Payment Method" value={f.paymentMethod ? <span className="capitalize">{f.paymentMethod.replace(/_/g, " ")}</span> : null} />
        <DetailRow label="Record Date" value={fmtDate(f.recordDate || f.createdAt)} />
        <DetailRow label="Paid At" value={fmtDate(f.paidAt)} />
        <DetailRow label="Created" value={fmtDate(f.createdAt)} />
        {f.reservationId && (
          <DetailRow label="Linked Reservation" value={<span className="text-primary">#{f.reservationId}</span>} />
        )}
        {f.cleaningJobId && (
          <DetailRow label="Linked Cleaning Job" value={<span className="text-primary">#{f.cleaningJobId}</span>} />
        )}
        {f.maintenanceJobId && (
          <DetailRow label="Linked Maintenance Job" value={<span className="text-primary">#{f.maintenanceJobId}</span>} />
        )}
        {f.description && (
          <div className="py-2">
            <p className="text-sm text-muted-foreground mb-1">Description</p>
            <p className="text-sm leading-relaxed">{f.description}</p>
          </div>
        )}
      </div>
      <div className="mt-6">
        <Link href="/admin/finances">
          <Button variant="outline" className="w-full gap-2" data-testid="btn-view-financial-full">
            <ExternalLink className="w-4 h-4" /> View Full Financial Records
          </Button>
        </Link>
      </div>
    </>
  );
}

function ReservationDetailPanel({ record: r, statusBadge, fmt, fmtDate }: { record: any; statusBadge: (s: string) => React.ReactNode; fmt: (n: number) => string; fmtDate: (d: any) => string }) {
  return (
    <>
      <SheetHeader className="mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Calendar className="w-5 h-5 text-primary" />
          </div>
          <div>
            <SheetTitle>{r.guestName || "Reservation"}</SheetTitle>
            <p className="text-sm text-muted-foreground">Reservation #{r.id}</p>
          </div>
        </div>
      </SheetHeader>
      <Separator />
      <div className="mt-4 divide-y">
        <DetailRow label="Status" value={statusBadge(r.status)} />
        <DetailRow label="Guest Name" value={r.guestName || null} />
        {r.guestEmail && <DetailRow label="Guest Email" value={r.guestEmail} />}
        {r.guestPhone && <DetailRow label="Guest Phone" value={r.guestPhone} />}
        <DetailRow label="Check-in" value={fmtDate(r.checkInDate)} />
        <DetailRow label="Check-out" value={fmtDate(r.checkOutDate)} />
        <DetailRow label="Source" value={r.source ? <span className="capitalize">{r.source.replace(/_/g, " ")}</span> : null} />
        <DetailRow label="Amount Paid" value={r.paidAmount ? <span className="font-semibold">{fmt(parseFloat(r.paidAmount))}</span> : null} />
        {r.taxAmount && parseFloat(r.taxAmount) > 0 && (
          <DetailRow label={r.taxLabel || "Tax"} value={fmt(parseFloat(r.taxAmount))} />
        )}
        {r.upsellTotal && parseFloat(r.upsellTotal) > 0 && (
          <DetailRow label="Upsells" value={fmt(parseFloat(r.upsellTotal))} />
        )}
        <DetailRow label="Payment Method" value={r.paymentMethod ? <span className="capitalize">{r.paymentMethod.replace(/_/g, " ")}</span> : null} />
        <DetailRow label="Payment Status" value={r.paymentStatus ? statusBadge(r.paymentStatus) : null} />
        <DetailRow label="Created" value={fmtDate(r.createdAt)} />
        {r.notes && (
          <div className="py-2">
            <p className="text-sm text-muted-foreground mb-1">Notes</p>
            <p className="text-sm leading-relaxed">{r.notes}</p>
          </div>
        )}
      </div>
      <div className="mt-6">
        <Link href="/admin/reservations">
          <Button variant="outline" className="w-full gap-2" data-testid="btn-view-reservation-full">
            <ExternalLink className="w-4 h-4" /> View Full Reservations
          </Button>
        </Link>
      </div>
    </>
  );
}

// ── QA Zones Tab ─────────────────────────────────────────────────────────────

function QaZonesTab({ unitId }: { unitId: number }) {
  const { toast } = useToast();
  const { data: criteria = [], isLoading: criteriaLoading } = useQaCriteria(unitId);
  const { data: templates = [] } = useQaTemplates();
  const updateCriteria = useUpdateQaCriteria(unitId);
  const clearZones = useClearUnitZones();
  const applyTemplate = useApplyQaTemplate();

  // Local zone state for editing
  type ZoneRow = { zoneName: string; zoneOrder: number; criteria: { requiredItems: string; cleanlinessNotes: string; stagingNotes: string; amenityNotes: string }; expanded: boolean };
  const [zones, setZones] = useState<ZoneRow[]>([]);
  const [newZoneName, setNewZoneName] = useState("");
  const [dirty, setDirty] = useState(false);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<number | "">("");

  // Sync criteria → local state when loaded
  useEffect(() => {
    if (criteriaLoading) return;
    const sorted = [...(criteria as any[])].sort((a, b) => (a.zoneName as string).localeCompare(b.zoneName));
    setZones(sorted.map((c: any) => ({
      zoneName: c.zoneName,
      zoneOrder: c.zoneOrder ?? 0,
      criteria: {
        requiredItems: c.criteriaJson?.requiredItems?.join(", ") ?? "",
        cleanlinessNotes: c.criteriaJson?.cleanlinessNotes ?? "",
        stagingNotes: c.criteriaJson?.stagingNotes ?? "",
        amenityNotes: c.criteriaJson?.amenityNotes ?? "",
      },
      expanded: false,
    })));
    setDirty(false);
  }, [criteria, criteriaLoading]);

  const addZone = () => {
    const slug = newZoneName.trim().toLowerCase().replace(/\s+/g, "_");
    if (!slug) return;
    setZones(z => [...z, { zoneName: slug, zoneOrder: z.length, criteria: { requiredItems: "", cleanlinessNotes: "", stagingNotes: "", amenityNotes: "" }, expanded: true }]);
    setNewZoneName("");
    setDirty(true);
  };

  const moveZone = (index: number, dir: "up" | "down") => {
    setZones(z => {
      const arr = [...z];
      const swap = dir === "up" ? index - 1 : index + 1;
      [arr[index], arr[swap]] = [arr[swap], arr[index]];
      return arr.map((row, i) => ({ ...row, zoneOrder: i }));
    });
    setDirty(true);
  };

  const handleSave = async () => {
    const criteriaMap: Record<string, any> = {};
    for (const zone of zones) {
      const c: Record<string, any> = {};
      const items = zone.criteria.requiredItems.split(",").map((s: string) => s.trim()).filter(Boolean);
      if (items.length) c.requiredItems = items;
      if (zone.criteria.cleanlinessNotes.trim()) c.cleanlinessNotes = zone.criteria.cleanlinessNotes.trim();
      if (zone.criteria.stagingNotes.trim()) c.stagingNotes = zone.criteria.stagingNotes.trim();
      if (zone.criteria.amenityNotes.trim()) c.amenityNotes = zone.criteria.amenityNotes.trim();
      criteriaMap[zone.zoneName] = c;
    }
    try {
      await updateCriteria.mutateAsync(criteriaMap);
      toast({ title: "QA zones saved" });
      setDirty(false);
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleClear = async () => {
    try {
      await clearZones.mutateAsync(unitId);
      toast({ title: "Zone configuration cleared — using auto-generated defaults" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplate) return;
    try {
      await applyTemplate.mutateAsync({ templateId: Number(selectedTemplate), unitIds: [unitId] });
      toast({ title: "Template applied" });
      setApplyDialogOpen(false);
      setSelectedTemplate("");
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  if (criteriaLoading) {
    return <div className="py-8 text-center text-sm text-gray-400">Loading…</div>;
  }

  const isCustom = (criteria as any[]).length > 0;

  return (
    <div className="space-y-4 py-2">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">
            {isCustom ? `Custom configuration — ${zones.length} zone${zones.length !== 1 ? "s" : ""}` : "Auto-generated from bedroom/bathroom count"}
          </p>
        </div>
        <div className="flex gap-2">
          {isCustom && (
            <Button variant="outline" size="sm" onClick={handleClear} disabled={clearZones.isPending}>
              Reset to auto
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setApplyDialogOpen(true)} disabled={templates.length === 0}>
            Apply Template
          </Button>
          {dirty && (
            <Button size="sm" onClick={handleSave} disabled={updateCriteria.isPending}>
              {updateCriteria.isPending ? "Saving…" : "Save"}
            </Button>
          )}
        </div>
      </div>

      {!isCustom && (
        <p className="text-xs text-gray-400 bg-gray-50 border rounded px-3 py-2">
          No custom zones configured. The AI will scan auto-generated zones based on this unit's bedroom/bathroom count. Apply a template or add zones below to customize.
        </p>
      )}

      <div className="space-y-2">
        {zones.map((zone, i) => (
          <div key={i} className="border rounded-md">
            <div className="flex items-center gap-2 px-3 py-2">
              <div className="flex flex-col gap-0.5">
                <button type="button" onClick={() => moveZone(i, "up")} disabled={i === 0} className="text-gray-400 hover:text-gray-600 disabled:opacity-20">
                  <ArrowUp className="w-3 h-3" />
                </button>
                <button type="button" onClick={() => moveZone(i, "down")} disabled={i === zones.length - 1} className="text-gray-400 hover:text-gray-600 disabled:opacity-20">
                  <ArrowDown className="w-3 h-3" />
                </button>
              </div>
              <span className="flex-1 text-sm font-medium">{zone.zoneName.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span>
              <button type="button" onClick={() => { setZones(z => z.map((row, idx) => idx === i ? { ...row, expanded: !row.expanded } : row)); }} className="text-gray-400 hover:text-gray-600">
                {zone.expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              <button type="button" onClick={() => { setZones(z => z.filter((_, idx) => idx !== i).map((row, j) => ({ ...row, zoneOrder: j }))); setDirty(true); }} className="text-gray-400 hover:text-red-500">
                <X className="w-4 h-4" />
              </button>
            </div>
            {zone.expanded && (
              <div className="px-3 pb-3 space-y-2 border-t pt-2">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Required items (comma-separated)</label>
                  <Input value={zone.criteria.requiredItems} onChange={e => { setZones(z => z.map((row, idx) => idx === i ? { ...row, criteria: { ...row.criteria, requiredItems: e.target.value } } : row)); setDirty(true); }} placeholder="e.g. toilet paper, hand soap" className="h-8 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Cleanliness notes</label>
                  <Textarea value={zone.criteria.cleanlinessNotes} onChange={e => { setZones(z => z.map((row, idx) => idx === i ? { ...row, criteria: { ...row.criteria, cleanlinessNotes: e.target.value } } : row)); setDirty(true); }} className="text-sm resize-none" rows={2} />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Staging notes</label>
                  <Textarea value={zone.criteria.stagingNotes} onChange={e => { setZones(z => z.map((row, idx) => idx === i ? { ...row, criteria: { ...row.criteria, stagingNotes: e.target.value } } : row)); setDirty(true); }} className="text-sm resize-none" rows={2} />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Amenity notes</label>
                  <Textarea value={zone.criteria.amenityNotes} onChange={e => { setZones(z => z.map((row, idx) => idx === i ? { ...row, criteria: { ...row.criteria, amenityNotes: e.target.value } } : row)); setDirty(true); }} className="text-sm resize-none" rows={2} />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Input
          value={newZoneName}
          onChange={e => setNewZoneName(e.target.value)}
          placeholder="Add zone (e.g. rooftop, pool_area)"
          className="h-8 text-sm"
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addZone(); } }}
        />
        <Button type="button" variant="outline" size="sm" onClick={addZone} className="shrink-0">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>

      {dirty && (
        <Button className="w-full" onClick={handleSave} disabled={updateCriteria.isPending}>
          {updateCriteria.isPending ? "Saving…" : "Save Zone Configuration"}
        </Button>
      )}

      {/* Apply template dialog */}
      <Dialog open={applyDialogOpen} onOpenChange={o => { if (!o) { setApplyDialogOpen(false); setSelectedTemplate(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Apply Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              This will replace the current zone configuration for this unit.
            </p>
            <select
              className="w-full border rounded-md h-9 px-3 text-sm"
              value={selectedTemplate}
              onChange={e => setSelectedTemplate(e.target.value ? Number(e.target.value) : "")}
            >
              <option value="">Select a template…</option>
              {(templates as any[]).map((t: any) => (
                <option key={t.id} value={t.id}>{t.name} ({(t.zonesJson ?? []).length} zones)</option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleApplyTemplate} disabled={!selectedTemplate || applyTemplate.isPending}>
              {applyTemplate.isPending ? "Applying…" : "Apply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminApartmentDetail() {
  const [, params] = useRoute("/admin/apartments/:id");
  const id = params?.id ? parseInt(params.id) : 0;
  const { data: apartment, isLoading: aptLoading } = useApartment(id);
  const { data: buildings } = useBuildings();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const createChecklistItem = useCreateChecklistItem();
  const createPhotoRequirement = useCreatePhotoRequirement();
  const updateApartment = useUpdateApartment();

  const [detailTab, setDetailTab] = useState<"details" | "inventory" | "activity" | "costs" | "qa" | "laundry">("details");
  const [checklistDialogOpen, setChecklistDialogOpen] = useState(false);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [editInfoOpen, setEditInfoOpen] = useState(false);
  const [icalUrlInput, setIcalUrlInput] = useState("");
  const [icalSaving, setIcalSaving] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState<{ created: number; updated: number; cancelled: number; errors: string[] } | null>(null);
  const [infoForm, setInfoForm] = useState({ wifiSsid: "", wifiPassword: "", checkoutTime: "", fobInstructions: "", fobRequired: true, heroImage: "", bedrooms: 1 });
  const [isUploading, setIsUploading] = useState(false);
  const [marketplaceForm, setMarketplaceForm] = useState({
    marketplaceVisible: false,
    location: "",
    marketplacePrice: "",
    marketplaceDescription: "",
    marketplaceFeatured: false,
    propertyType: "apartment",
    amenities: "",
  });
  const [editMarketplaceOpen, setEditMarketplaceOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deleteChecklistConfirm, setDeleteChecklistConfirm] = useState<ChecklistItem | null>(null);
  const [deletePhotoReqConfirm, setDeletePhotoReqConfirm] = useState<PhotoRequirement | null>(null);
  const [deleteApartmentPhotoConfirmId, setDeleteApartmentPhotoConfirmId] = useState<number | null>(null);

  const { data: apartmentPhotos, isLoading: apartmentPhotosLoading } = useQuery<ApartmentPhoto[]>({
    queryKey: ['/api/admin/apartments', id, 'photos'],
    enabled: !!id,
  });

  const { data: allUsers } = useAdminUsers();
  const ownerUsers = (allUsers || []).filter((u: any) => u.role === 'owner');

  const { data: apartmentOwnersData } = useQuery<{ id: number; apartmentId: number; userId: number; role: string | null; createdAt: string | null }[]>({
    queryKey: ['/api/apartments', id, 'owners'],
    enabled: !!id,
  });

  const icalSyncMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', `/api/admin/apartments/${id}/ical-sync`, {});
      return res.json() as Promise<{ created: number; updated: number; cancelled: number; errors: string[] }>;
    },
    onSuccess: (data) => {
      setLastSyncResult(data);
      queryClient.invalidateQueries({ queryKey: ['/api/apartments', id] });
      queryClient.invalidateQueries({ queryKey: ['/api/apartments'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/reservations'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/cleaning-jobs'] });
      const msg = `${data.created} created, ${data.updated} updated, ${data.cancelled} cancelled`;
      toast({ title: "Sync complete", description: msg });
    },
    onError: (err: any) => {
      const msg = err?.message || "Could not sync iCal calendar";
      toast({ title: "Sync failed", description: msg, variant: "destructive" });
    },
  });

  const addOwnerMutation = useMutation({
    mutationFn: async (userId: number) => {
      await apiRequest('POST', `/api/apartments/${id}/owners`, { userId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/apartments', id, 'owners'] });
      toast({ title: "Property Manager added" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to add property manager", variant: "destructive" });
    },
  });

  const removeOwnerMutation = useMutation({
    mutationFn: async (userId: number) => {
      await apiRequest('DELETE', `/api/apartments/${id}/owners/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/apartments', id, 'owners'] });
      toast({ title: "Property Manager removed" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to remove property manager", variant: "destructive" });
    },
  });

  // Auto-seed: if the apartment has ownerUserId but no owners in the table yet, add it automatically
  const seededRef = useRef(false);
  useEffect(() => {
    if (
      !seededRef.current &&
      apartment?.ownerUserId &&
      apartmentOwnersData !== undefined &&
      apartmentOwnersData.length === 0
    ) {
      seededRef.current = true;
      addOwnerMutation.mutate(apartment.ownerUserId);
    }
  }, [apartment?.ownerUserId, apartmentOwnersData]);

  const deleteApartmentPhoto = useMutation({
    mutationFn: async (photoId: number) => {
      await apiRequest('DELETE', `/api/admin/apartments/${id}/photos/${photoId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/apartments', id, 'photos'] });
      toast({ title: "Photo deleted" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete photo", variant: "destructive" });
    },
  });

  const setCoverPhoto = useMutation({
    mutationFn: async (photoId: number) => {
      if (apartmentPhotos) {
        for (const photo of apartmentPhotos) {
          if (photo.isCover && photo.id !== photoId) {
            await apiRequest('PUT', `/api/admin/apartments/${id}/photos/${photo.id}`, { isCover: false });
          }
        }
      }
      await apiRequest('PUT', `/api/admin/apartments/${id}/photos/${photoId}`, { isCover: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/apartments', id, 'photos'] });
      toast({ title: "Cover photo updated" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to set cover photo", variant: "destructive" });
    },
  });

  const handlePhotoUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setIsUploading(true);
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append('file', file);
        const uploadRes = await fetch('/api/upload', { method: 'POST', body: formData, credentials: 'include' });
        if (!uploadRes.ok) throw new Error('Upload failed');
        const { url } = await uploadRes.json();
        await apiRequest('POST', `/api/admin/apartments/${id}/photos`, { url, isCover: false });
      }
      queryClient.invalidateQueries({ queryKey: ['/api/admin/apartments', id, 'photos'] });
      toast({ title: "Photos uploaded", description: `${files.length} photo(s) uploaded successfully` });
    } catch {
      toast({ title: "Error", description: "Failed to upload photos", variant: "destructive" });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const { data: checklistItems, isLoading: checklistLoading } = useQuery<ChecklistItem[]>({
    queryKey: ['/api/admin/checklist', id],
    queryFn: async () => {
      const aptRes = await fetch(`/api/apartments/${id}`);
      const apt = await aptRes.json();
      const guestRes = await fetch(`/api/g/${apt.slug}?t=${apt.publicToken}`);
      const guestData = await guestRes.json();
      return guestData.checklistItems;
    },
    enabled: !!id,
  });

  const { data: photoRequirements, isLoading: photosLoading } = useQuery<PhotoRequirement[]>({
    queryKey: ['/api/admin/photo-requirements', id],
    queryFn: async () => {
      const aptRes = await fetch(`/api/apartments/${id}`);
      const apt = await aptRes.json();
      const guestRes = await fetch(`/api/g/${apt.slug}?t=${apt.publicToken}`);
      const guestData = await guestRes.json();
      return guestData.photoRequirements;
    },
    enabled: !!id,
  });

  const deleteChecklistItem = async (itemId: number) => {
    try {
      await fetch(`/api/admin/checklist/${itemId}`, { method: 'DELETE' });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/checklist', id] });
      toast({ title: "Checklist item deleted" });
    } catch {
      toast({ title: "Error", description: "Failed to delete checklist item", variant: "destructive" });
    }
  };

  const deletePhotoRequirement = async (itemId: number) => {
    try {
      await fetch(`/api/admin/photo-requirements/${itemId}`, { method: 'DELETE' });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/photo-requirements', id] });
      toast({ title: "Photo requirement deleted" });
    } catch {
      toast({ title: "Error", description: "Failed to delete photo requirement", variant: "destructive" });
    }
  };

  const checklistForm = useForm<z.infer<typeof checklistFormSchema>>({
    resolver: zodResolver(checklistFormSchema),
    defaultValues: {
      textEn: "",
      textEs: "",
      apartmentId: id,
      isRequired: true,
    },
  });

  const photoForm = useForm<z.infer<typeof photoFormSchema>>({
    resolver: zodResolver(photoFormSchema),
    defaultValues: {
      labelEn: "",
      labelEs: "",
      instructionsEn: "",
      instructionsEs: "",
      apartmentId: id,
      minPhotos: 1,
      isRequired: true,
    },
  });

  async function onSubmitChecklist(values: z.infer<typeof checklistFormSchema>) {
    try {
      await createChecklistItem.mutateAsync({ ...values, apartmentId: id });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/checklist', id] });
      toast({ title: "Checklist item added" });
      setChecklistDialogOpen(false);
      checklistForm.reset({ textEn: "", textEs: "", apartmentId: id, isRequired: true });
    } catch {
      toast({ title: "Error", description: "Failed to create checklist item", variant: "destructive" });
    }
  }

  async function onSubmitPhoto(values: z.infer<typeof photoFormSchema>) {
    try {
      await createPhotoRequirement.mutateAsync({ ...values, apartmentId: id });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/photo-requirements', id] });
      toast({ title: "Photo requirement added" });
      setPhotoDialogOpen(false);
      photoForm.reset({ labelEn: "", labelEs: "", instructionsEn: "", instructionsEs: "", apartmentId: id, minPhotos: 1, isRequired: true });
    } catch {
      toast({ title: "Error", description: "Failed to create photo requirement", variant: "destructive" });
    }
  }

  const building = buildings?.find(b => b.id === apartment?.buildingId);

  useEffect(() => {
    if (apartment) {
      setIcalUrlInput((apartment as any).icalUrl || "");
      if ((apartment as any).lastSyncResult) {
        setLastSyncResult((apartment as any).lastSyncResult);
      }
    }
  }, [apartment?.id]);

  const getGuestLink = () => {
    if (!apartment) return "";
    return `${window.location.origin}/g/${apartment.slug}?t=${apartment.publicToken}`;
  };

  if (aptLoading) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-60 w-full" />
        </div>
      </AdminLayout>
    );
  }

  if (!apartment) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Apartment not found.</p>
          <Link href="/admin/apartments">
            <Button variant="outline" className="mt-4" data-testid="link-back-apartments">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Apartments
            </Button>
          </Link>
        </div>
      </AdminLayout>
    );
  }

  const coverPhoto = apartmentPhotos?.find(p => p.isCover) || apartmentPhotos?.[0];
  const initials = apartment.name.replace(/[^a-zA-Z0-9\s]/g, "").split(/\s+/).map((w: string) => w[0] || "").slice(0, 2).join("").toUpperCase() || "AP";

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Visual profile card header */}
        <div className="rounded-2xl overflow-hidden border bg-card shadow-sm" data-testid="apartment-profile-card">
          {/* Banner */}
          <div className="relative h-32 md:h-44">
            {coverPhoto ? (
              <img src={coverPhoto.url} alt={apartment.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"
                style={{ background: "linear-gradient(135deg, #7c3a1e 0%, #c2652a 50%, #e8883a 100%)" }}>
                <span className="text-white/20 font-display font-black text-8xl select-none tracking-tight">{initials}</span>
              </div>
            )}
            {/* Back button overlay */}
            <div className="absolute top-3 left-3">
              <Link href="/admin/apartments">
                <Button variant="ghost" size="icon" className="bg-black/40 hover:bg-black/60 border-0 text-white backdrop-blur-sm rounded-full" data-testid="button-back">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              </Link>
            </div>
            {/* Quick guest link button */}
            <div className="absolute top-3 right-3">
              <Button variant="ghost" size="sm"
                className="bg-black/40 hover:bg-black/60 border-0 text-white text-xs backdrop-blur-sm gap-1.5 rounded-full px-3"
                onClick={() => { navigator.clipboard.writeText(getGuestLink()); toast({ title: "Guest link copied!" }); }}
                data-testid="button-copy-guest-link">
                <Link2 className="w-3.5 h-3.5" />
                Copy Guest Link
              </Button>
            </div>
          </div>

          {/* Profile info below banner */}
          <div className="px-5 py-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <h1 className="text-2xl font-display font-bold leading-tight" data-testid="text-apartment-name">{apartment.name}</h1>
                <p className="text-muted-foreground text-sm mt-0.5" data-testid="text-building-name">
                  {building?.name || "Unknown Building"}
                </p>
                {(() => {
                  const addr = (building as any)?.address || (apartment as any)?.location;
                  if (!addr) return null;
                  return (
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(addr)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary mt-0.5"
                      data-testid="link-apartment-address"
                    >
                      <MapPin className="w-3 h-3 shrink-0" />{addr}
                    </a>
                  );
                })()}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <Badge variant="secondary" className="gap-1.5 text-xs">
                <BedDouble className="w-3 h-3" />
                {(apartment as any).bedrooms === 0 ? "Studio" : `${(apartment as any).bedrooms} Bedroom${(apartment as any).bedrooms !== 1 ? "s" : ""}`}
              </Badge>
              {apartmentOwnersData && apartmentOwnersData.length > 0 && (
                <Badge variant="secondary" className="gap-1.5 text-xs">
                  <Users className="w-3 h-3" />
                  {apartmentOwnersData.length} Property Manager{apartmentOwnersData.length !== 1 ? "s" : ""}
                </Badge>
              )}
              {(apartment as any).marketplaceVisible && (
                <Badge className="gap-1.5 text-xs bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800">
                  <Store className="w-3 h-3" />
                  Listed on Marketplace
                </Badge>
              )}
              {(apartment as any).wifiSsid && (
                <Badge variant="outline" className="gap-1.5 text-xs text-muted-foreground">
                  <Wifi className="w-3 h-3" />
                  {(apartment as any).wifiSsid}
                </Badge>
              )}
              {(apartment as any).checkoutTime && (
                <Badge variant="outline" className="gap-1.5 text-xs text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  Checkout {(apartment as any).checkoutTime}
                </Badge>
              )}
              {(apartment as any).fobRequired && (
                <Badge variant="outline" className="gap-1.5 text-xs text-muted-foreground">
                  <Key className="w-3 h-3" />
                  FOB Required
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-1 border-b overflow-x-auto" data-testid="apartment-detail-tabs">
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${detailTab === "details" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            onClick={() => setDetailTab("details")}
            data-testid="tab-details"
          >
            Details
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${detailTab === "inventory" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            onClick={() => setDetailTab("inventory")}
            data-testid="tab-inventory"
          >
            <Package className="w-4 h-4" />
            Inventory
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${detailTab === "activity" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            onClick={() => setDetailTab("activity")}
            data-testid="tab-activity"
          >
            <Calendar className="w-4 h-4" />
            Activity
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${detailTab === "costs" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            onClick={() => setDetailTab("costs")}
            data-testid="tab-costs"
          >
            <Receipt className="w-4 h-4" />
            Costs
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${detailTab === "qa" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            onClick={() => setDetailTab("qa")}
            data-testid="tab-qa"
          >
            <ScanLine className="w-4 h-4" />
            QA Zones
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${detailTab === "laundry" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            onClick={() => setDetailTab("laundry")}
            data-testid="tab-laundry"
          >
            <Wind className="w-4 h-4" />
            Laundry
          </button>
        </div>

        {detailTab === "costs" && (
          <div className="space-y-6">
            <ApartmentCostsSection apartmentId={id} />
            <ApartmentCleaningSuppliesSection apartmentId={id} apartment={apartment} />
          </div>
        )}

        {detailTab === "qa" && <QaZonesTab unitId={id} />}

        {detailTab === "laundry" && <ApartmentLaundryItemsSection apartmentId={id} apartment={apartment} />}

        {detailTab === "inventory" && (
          <ApartmentInventorySection apartmentId={id} />
        )}

        {detailTab === "activity" && (
          <ApartmentActivitySection apartmentId={id} />
        )}

        {detailTab === "details" && (<>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-2">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Guest Link</Label>
            </div>
            <div className="flex gap-2">
              <Input
                readOnly
                value={getGuestLink()}
                className="font-mono text-xs bg-muted/50"
                data-testid="input-guest-link"
              />
              <Button
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(getGuestLink());
                  toast({ title: "Copied to clipboard" });
                }}
                data-testid="button-copy-link"
              >
                <Copy className="w-4 h-4 mr-2" /> Copy
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5" />
              Property Managers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(apartmentOwnersData || []).length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="text-no-owners">No property managers assigned</p>
              )}
              {(apartmentOwnersData || []).map((ao) => {
                const user = ownerUsers.find((u: any) => u.id === ao.userId);
                return (
                  <div key={ao.id} className="flex items-center justify-between p-2 border rounded-lg" data-testid={`row-owner-${ao.userId}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{user ? (user.fullName || user.username) : `User #${ao.userId}`}</span>
                      <Link href={`/admin/pm/${ao.userId}`}>
                        <button className="text-xs text-primary hover:underline flex items-center gap-1" data-testid={`btn-pm-profile-${ao.userId}`}>
                          <ChevronRight className="w-3 h-3" /> View Profile
                        </button>
                      </Link>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeOwnerMutation.mutate(ao.userId)}
                      data-testid={`button-remove-owner-${ao.userId}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
              <div className="pt-2">
                <SearchableSelect
                  value=""
                  onValueChange={(val) => {
                    if (val) addOwnerMutation.mutate(Number(val));
                  }}
                  placeholder="Add a Property Manager..."
                  searchPlaceholder="Search property manager..."
                  options={ownerUsers
                    .filter((u: any) => u.isActive !== false && !(apartmentOwnersData || []).some((ao) => ao.userId === u.id))
                    .map((u: any) => ({ value: String(u.id), label: u.fullName || u.username }))}
                  className="w-full"
                  data-testid="select-add-owner"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <CardTitle className="text-lg">Apartment Info</CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setInfoForm({
                  wifiSsid: apartment.wifiSsid || "",
                  wifiPassword: apartment.wifiPassword || "",
                  checkoutTime: apartment.checkoutTime || "",
                  fobInstructions: apartment.fobInstructions || "",
                  fobRequired: apartment.fobRequired ?? true,
                  heroImage: apartment.heroImage || "",
                  bedrooms: apartment.bedrooms ?? 1,
                });
                setEditInfoOpen(true);
              }}
              data-testid="button-edit-info"
            >
              <Pencil className="w-4 h-4 mr-2" /> Edit
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-md text-primary">
                  <Wifi className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">WiFi</p>
                  <p className="text-sm font-medium" data-testid="text-wifi-ssid">{apartment.wifiSsid || "N/A"}</p>
                  <p className="text-xs text-muted-foreground font-mono" data-testid="text-wifi-password">{apartment.wifiPassword || ""}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-md text-primary">
                  <Clock className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Checkout Time</p>
                  <p className="text-sm font-medium" data-testid="text-checkout-time">{apartment.checkoutTime || "N/A"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-md text-primary">
                  <Key className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">FOB Required</p>
                  <Badge variant={apartment.fobRequired ? "default" : "secondary"} data-testid="badge-fob-required">
                    {apartment.fobRequired ? "Yes" : "No"}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-md text-primary">
                  <BedDouble className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Bedrooms</p>
                  <p className="text-sm font-medium" data-testid="text-bedrooms">
                    {apartment.bedrooms === 0 ? "Studio" : `${apartment.bedrooms ?? 1} BR`}
                  </p>
                </div>
              </div>
            </div>
            {apartment.fobInstructions && (
              <div className="pt-3 border-t">
                <p className="text-xs text-muted-foreground mb-1">FOB Instructions</p>
                <p className="text-sm" data-testid="text-fob-instructions">{apartment.fobInstructions}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={editInfoOpen} onOpenChange={setEditInfoOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Apartment Info</DialogTitle>
              <DialogDescription>Update apartment details</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">WiFi Name</Label>
                  <Input value={infoForm.wifiSsid} onChange={e => setInfoForm({ ...infoForm, wifiSsid: e.target.value })} data-testid="input-edit-wifi-ssid" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">WiFi Password</Label>
                  <Input value={infoForm.wifiPassword} onChange={e => setInfoForm({ ...infoForm, wifiPassword: e.target.value })} data-testid="input-edit-wifi-password" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Checkout Time</Label>
                {(() => {
                  const presetTimes = ["09:00","09:30","10:00","10:30","11:00","11:30","12:00","12:30","13:00","14:00","15:00"];
                  const legacyValue = infoForm.checkoutTime && !presetTimes.includes(infoForm.checkoutTime) ? infoForm.checkoutTime : null;
                  return (
                    <Select value={infoForm.checkoutTime || ""} onValueChange={v => setInfoForm({ ...infoForm, checkoutTime: v })}>
                      <SelectTrigger data-testid="select-edit-checkout-time">
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent>
                        {legacyValue && <SelectItem value={legacyValue}>{legacyValue} (current)</SelectItem>}
                        <SelectItem value="09:00">9:00 AM</SelectItem>
                        <SelectItem value="09:30">9:30 AM</SelectItem>
                        <SelectItem value="10:00">10:00 AM</SelectItem>
                        <SelectItem value="10:30">10:30 AM</SelectItem>
                        <SelectItem value="11:00">11:00 AM</SelectItem>
                        <SelectItem value="11:30">11:30 AM</SelectItem>
                        <SelectItem value="12:00">12:00 PM (Noon)</SelectItem>
                        <SelectItem value="12:30">12:30 PM</SelectItem>
                        <SelectItem value="13:00">1:00 PM</SelectItem>
                        <SelectItem value="14:00">2:00 PM</SelectItem>
                        <SelectItem value="15:00">3:00 PM</SelectItem>
                      </SelectContent>
                    </Select>
                  );
                })()}
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">FOB Instructions</Label>
                <Textarea value={infoForm.fobInstructions} onChange={e => setInfoForm({ ...infoForm, fobInstructions: e.target.value })} data-testid="input-edit-fob-instructions" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Hero Image URL</Label>
                <Input value={infoForm.heroImage} onChange={e => setInfoForm({ ...infoForm, heroImage: e.target.value })} data-testid="input-edit-hero-image" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Bedrooms</Label>
                <Select value={String(infoForm.bedrooms)} onValueChange={v => setInfoForm({ ...infoForm, bedrooms: Number(v) })}>
                  <SelectTrigger data-testid="select-edit-bedrooms">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Studio</SelectItem>
                    <SelectItem value="1">1 Bedroom</SelectItem>
                    <SelectItem value="2">2 Bedrooms</SelectItem>
                    <SelectItem value="3">3 Bedrooms</SelectItem>
                    <SelectItem value="4">4 Bedrooms</SelectItem>
                    <SelectItem value="5">5 Bedrooms</SelectItem>
                    <SelectItem value="6">6 Bedrooms</SelectItem>
                    <SelectItem value="7">7 Bedrooms</SelectItem>
                    <SelectItem value="8">8 Bedrooms</SelectItem>
                    <SelectItem value="9">9 Bedrooms</SelectItem>
                    <SelectItem value="10">10+ Bedrooms</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={infoForm.fobRequired} onCheckedChange={v => setInfoForm({ ...infoForm, fobRequired: v })} data-testid="switch-edit-fob-required" />
                <Label className="text-sm font-medium">FOB Required</Label>
              </div>
              <Button
                className="w-full"
                disabled={updateApartment.isPending}
                onClick={async () => {
                  try {
                    await updateApartment.mutateAsync({ id, data: infoForm });
                    queryClient.invalidateQueries({ queryKey: [api.apartments.get.path, id] });
                    queryClient.invalidateQueries({ queryKey: [api.apartments.list.path] });
                    toast({ title: "Apartment info updated" });
                    setEditInfoOpen(false);
                  } catch {
                    toast({ title: "Error", description: "Failed to update", variant: "destructive" });
                  }
                }}
                data-testid="button-save-info"
              >
                {updateApartment.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Store className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">Marketplace Settings</CardTitle>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setMarketplaceForm({
                  marketplaceVisible: apartment.marketplaceVisible ?? false,
                  location: apartment.location || "",
                  marketplacePrice: apartment.marketplacePrice || "",
                  marketplaceDescription: apartment.marketplaceDescription || "",
                  marketplaceFeatured: apartment.marketplaceFeatured ?? false,
                  propertyType: apartment.propertyType || "apartment",
                  amenities: apartment.amenities || "",
                });
                setEditMarketplaceOpen(true);
              }}
              data-testid="button-edit-marketplace"
            >
              <Pencil className="w-4 h-4 mr-2" /> Edit
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-md text-primary">
                  <Store className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Visibility</p>
                  <Badge variant={apartment.marketplaceVisible ? "default" : "secondary"} data-testid="badge-marketplace-visible">
                    {apartment.marketplaceVisible ? "Listed" : "Hidden"}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-md text-primary">
                  <MapPin className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Location</p>
                  <p className="text-sm font-medium" data-testid="text-marketplace-location">{apartment.location || "Not set"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-md text-primary">
                  <DollarSign className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Nightly Price</p>
                  <p className="text-sm font-medium" data-testid="text-marketplace-price">{apartment.marketplacePrice ? `$${apartment.marketplacePrice}` : "Not set"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-md text-primary">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Featured</p>
                  <Badge variant={apartment.marketplaceFeatured ? "default" : "secondary"} data-testid="badge-marketplace-featured">
                    {apartment.marketplaceFeatured ? "Promoted" : "Standard"}
                  </Badge>
                </div>
              </div>
            </div>
            {apartment.marketplaceDescription && (
              <div className="pt-3 border-t">
                <p className="text-xs text-muted-foreground mb-1">Description</p>
                <p className="text-sm line-clamp-3" data-testid="text-marketplace-description">{apartment.marketplaceDescription}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={editMarketplaceOpen} onOpenChange={setEditMarketplaceOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Marketplace Settings</DialogTitle>
              <DialogDescription>Configure marketplace visibility and listing details</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Switch
                  checked={marketplaceForm.marketplaceVisible}
                  onCheckedChange={v => setMarketplaceForm({ ...marketplaceForm, marketplaceVisible: v })}
                  data-testid="switch-edit-marketplace-visible"
                />
                <div className="flex items-center gap-1.5">
                  <Label className="text-sm font-medium">Show on Marketplace</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs">
                        When enabled, this property is listed on the public marketplace and can be booked directly by guests. Disable to keep it private or for internal use only.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Location</Label>
                <Input
                  placeholder="Miami Beach, FL"
                  value={marketplaceForm.location}
                  onChange={e => setMarketplaceForm({ ...marketplaceForm, location: e.target.value })}
                  data-testid="input-edit-marketplace-location"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Nightly Price ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="150.00"
                  value={marketplaceForm.marketplacePrice}
                  onChange={e => setMarketplaceForm({ ...marketplaceForm, marketplacePrice: e.target.value })}
                  data-testid="input-edit-marketplace-price"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Property Type</Label>
                <Select value={marketplaceForm.propertyType} onValueChange={v => setMarketplaceForm({ ...marketplaceForm, propertyType: v })}>
                  <SelectTrigger data-testid="select-edit-property-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apartment">Apartment</SelectItem>
                    <SelectItem value="house">House</SelectItem>
                    <SelectItem value="villa">Villa</SelectItem>
                    <SelectItem value="studio">Studio</SelectItem>
                    <SelectItem value="penthouse">Penthouse</SelectItem>
                    <SelectItem value="loft">Loft</SelectItem>
                    <SelectItem value="cabin">Cabin</SelectItem>
                    <SelectItem value="condo">Condo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Marketplace Description</Label>
                <Textarea
                  placeholder="Describe the property for potential guests..."
                  value={marketplaceForm.marketplaceDescription}
                  onChange={e => setMarketplaceForm({ ...marketplaceForm, marketplaceDescription: e.target.value })}
                  data-testid="input-edit-marketplace-description"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Amenities (comma-separated)</Label>
                <Input
                  placeholder="wifi,pool,gym,kitchen,parking,ac"
                  value={marketplaceForm.amenities}
                  onChange={e => setMarketplaceForm({ ...marketplaceForm, amenities: e.target.value })}
                  data-testid="input-edit-marketplace-amenities"
                />
              </div>
              <div className="flex items-center gap-3 border rounded-md p-3 bg-accent/30">
                <Switch
                  checked={marketplaceForm.marketplaceFeatured}
                  onCheckedChange={v => setMarketplaceForm({ ...marketplaceForm, marketplaceFeatured: v })}
                  data-testid="switch-edit-marketplace-featured"
                />
                <Label className="text-sm font-medium">Featured Listing (promoted)</Label>
              </div>
              <Button
                className="w-full"
                disabled={updateApartment.isPending}
                onClick={async () => {
                  try {
                    await updateApartment.mutateAsync({ id, data: marketplaceForm });
                    queryClient.invalidateQueries({ queryKey: [api.apartments.get.path, id] });
                    queryClient.invalidateQueries({ queryKey: [api.apartments.list.path] });
                    toast({ title: "Marketplace settings updated" });
                    setEditMarketplaceOpen(false);
                  } catch {
                    toast({ title: "Error", description: "Failed to update", variant: "destructive" });
                  }
                }}
                data-testid="button-save-marketplace"
              >
                {updateApartment.isPending ? "Saving..." : "Save Marketplace Settings"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">Calendar Sync</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Paste an iCal URL from Google Calendar, Airbnb, Booking.com, VRBO, Turno, or any platform that exports standard iCal. Reservations will sync automatically on schedule and can also be synced manually anytime.
            </p>
            <div className="flex gap-2">
              <Input
                placeholder="https://calendar.google.com/calendar/ical/..."
                value={icalUrlInput}
                onChange={e => setIcalUrlInput(e.target.value)}
                className="font-mono text-xs"
                data-testid="input-ical-url"
              />
              <Button
                variant="outline"
                disabled={icalSaving}
                onClick={async () => {
                  setIcalSaving(true);
                  try {
                    await updateApartment.mutateAsync({ id, data: { icalUrl: icalUrlInput || null } as any });
                    queryClient.invalidateQueries({ queryKey: ['/api/apartments', id] });
                    toast({ title: "iCal URL saved" });
                  } catch (err: any) {
                    const msg = err?.message || "Failed to save URL";
                    toast({ title: "Error saving iCal URL", description: msg, variant: "destructive" });
                  } finally {
                    setIcalSaving(false);
                  }
                }}
                data-testid="button-save-ical-url"
              >
                {icalSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </Button>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => icalSyncMutation.mutate()}
                disabled={icalSyncMutation.isPending || !icalUrlInput}
                data-testid="button-ical-sync-now"
              >
                {icalSyncMutation.isPending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Syncing...</>
                ) : (
                  <><RefreshCw className="w-4 h-4 mr-2" /> Sync Now</>
                )}
              </Button>
              {(apartment as any).lastSyncedAt && (
                <p className="text-xs text-muted-foreground" data-testid="text-last-synced">
                  Last synced {new Date((apartment as any).lastSyncedAt).toLocaleString()}
                </p>
              )}
            </div>
            {lastSyncResult && (
              <div className="rounded-md bg-muted/50 border p-3 text-sm" data-testid="text-sync-result">
                <span className="text-green-600 font-medium">{lastSyncResult.created} created</span>
                {" · "}
                <span className="text-blue-600 font-medium">{lastSyncResult.updated} updated</span>
                {" · "}
                <span className="text-orange-600 font-medium">{lastSyncResult.cancelled} cancelled</span>
                {lastSyncResult.errors.length > 0 && (
                  <p className="text-destructive mt-1 text-xs">{lastSyncResult.errors.slice(0, 3).join("; ")}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <CleaningOverrideCard apartment={apartment} id={id} />

        <UnitRulesCard id={id} />

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">Checklist Items</CardTitle>
              <Badge variant="secondary" className="ml-1">{checklistItems?.length ?? 0}</Badge>
            </div>
            <Dialog open={checklistDialogOpen} onOpenChange={setChecklistDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="button-add-checklist">
                  <Plus className="w-4 h-4 mr-2" /> Add Item
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Checklist Item</DialogTitle>
                  <DialogDescription>Add a new item to the cleaning checklist</DialogDescription>
                </DialogHeader>
                <Form {...checklistForm}>
                  <form onSubmit={checklistForm.handleSubmit(onSubmitChecklist)} className="space-y-4">
                    <FormField
                      control={checklistForm.control}
                      name="textEn"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Text (English)</FormLabel>
                          <FormControl><Input {...field} data-testid="input-checklist-text-en" /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={checklistForm.control}
                      name="textEs"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Text (Spanish)</FormLabel>
                          <FormControl><Input {...field} data-testid="input-checklist-text-es" /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={checklistForm.control}
                      name="isRequired"
                      render={({ field }) => (
                        <FormItem className="flex items-center gap-3">
                          <FormControl>
                            <Switch
                              checked={field.value ?? true}
                              onCheckedChange={field.onChange}
                              data-testid="switch-checklist-required"
                            />
                          </FormControl>
                          <FormLabel className="!mt-0">Required</FormLabel>
                        </FormItem>
                      )}
                    />
                    <Button type="submit" className="w-full" disabled={createChecklistItem.isPending} data-testid="button-submit-checklist">
                      {createChecklistItem.isPending ? "Adding..." : "Add Checklist Item"}
                    </Button>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {checklistLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : checklistItems && checklistItems.length > 0 ? (
              <div className="divide-y">
                {checklistItems.map((item: ChecklistItem) => (
                  <div
                    key={item.id}
                    className="group flex items-center justify-between gap-4 py-3"
                    data-testid={`checklist-item-${item.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium" data-testid={`text-checklist-en-${item.id}`}>{item.textEn}</p>
                      <p className="text-xs text-muted-foreground" data-testid={`text-checklist-es-${item.id}`}>{item.textEs}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.isRequired && <Badge variant="secondary" data-testid={`badge-required-${item.id}`}>Required</Badge>}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive invisible group-hover:visible"
                        data-testid={`button-delete-checklist-${item.id}`}
                        onClick={() => setDeleteChecklistConfirm(item)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-4 text-center">No checklist items yet.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">Photo Requirements</CardTitle>
              <Badge variant="secondary" className="ml-1">{photoRequirements?.length ?? 0}</Badge>
            </div>
            <Dialog open={photoDialogOpen} onOpenChange={setPhotoDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="button-add-photo">
                  <Plus className="w-4 h-4 mr-2" /> Add Requirement
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Add Photo Requirement</DialogTitle>
                  <DialogDescription>Add a required photo for cleaning verification</DialogDescription>
                </DialogHeader>
                <Form {...photoForm}>
                  <form onSubmit={photoForm.handleSubmit(onSubmitPhoto)} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={photoForm.control}
                        name="labelEn"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Label (English)</FormLabel>
                            <FormControl><Input {...field} data-testid="input-photo-label-en" /></FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={photoForm.control}
                        name="labelEs"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Label (Spanish)</FormLabel>
                            <FormControl><Input {...field} data-testid="input-photo-label-es" /></FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={photoForm.control}
                      name="instructionsEn"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Instructions (English)</FormLabel>
                          <FormControl><Input {...field} value={field.value ?? ""} data-testid="input-photo-instructions-en" /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={photoForm.control}
                      name="instructionsEs"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Instructions (Spanish)</FormLabel>
                          <FormControl><Input {...field} value={field.value ?? ""} data-testid="input-photo-instructions-es" /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={photoForm.control}
                      name="minPhotos"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Min Photos</FormLabel>
                          <FormControl><Input type="number" min={1} {...field} value={field.value ?? 1} data-testid="input-photo-min" /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={photoForm.control}
                      name="isRequired"
                      render={({ field }) => (
                        <FormItem className="flex items-center gap-3">
                          <FormControl>
                            <Switch
                              checked={field.value ?? true}
                              onCheckedChange={field.onChange}
                              data-testid="switch-photo-required"
                            />
                          </FormControl>
                          <FormLabel className="!mt-0">Required</FormLabel>
                        </FormItem>
                      )}
                    />
                    <Button type="submit" className="w-full" disabled={createPhotoRequirement.isPending} data-testid="button-submit-photo">
                      {createPhotoRequirement.isPending ? "Adding..." : "Add Photo Requirement"}
                    </Button>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {photosLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : photoRequirements && photoRequirements.length > 0 ? (
              <div className="divide-y">
                {photoRequirements.map((req: PhotoRequirement) => (
                  <div
                    key={req.id}
                    className="group flex items-center justify-between gap-4 py-3"
                    data-testid={`photo-requirement-${req.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium" data-testid={`text-photo-label-en-${req.id}`}>{req.labelEn}</p>
                        <Badge variant="outline" className="text-xs" data-testid={`badge-min-photos-${req.id}`}>
                          Min: {req.minPhotos}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground" data-testid={`text-photo-label-es-${req.id}`}>{req.labelEs}</p>
                      {req.instructionsEn && (
                        <p className="text-xs text-muted-foreground mt-1" data-testid={`text-photo-instructions-${req.id}`}>{req.instructionsEn}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {req.isRequired && <Badge variant="secondary" data-testid={`badge-photo-required-${req.id}`}>Required</Badge>}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive invisible group-hover:visible"
                        data-testid={`button-delete-photo-${req.id}`}
                        onClick={() => setDeletePhotoReqConfirm(req)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-4 text-center">No photo requirements yet.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">Photos</CardTitle>
              <Badge variant="secondary" className="ml-1">{apartmentPhotos?.length ?? 0}</Badge>
            </div>
            <Button
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              data-testid="button-upload-photos"
            >
              {isUploading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              {isUploading ? "Uploading..." : "Upload Photos"}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={(e) => handlePhotoUpload(e.target.files)}
              data-testid="input-photo-upload"
            />
          </CardHeader>
          <CardContent>
            {apartmentPhotosLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                <Skeleton className="aspect-square w-full" />
                <Skeleton className="aspect-square w-full" />
                <Skeleton className="aspect-square w-full" />
                <Skeleton className="aspect-square w-full" />
              </div>
            ) : apartmentPhotos && apartmentPhotos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {apartmentPhotos.map((photo) => (
                  <div
                    key={photo.id}
                    className="group relative rounded-md overflow-visible border"
                    data-testid={`photo-card-${photo.id}`}
                  >
                    <div className="aspect-square overflow-hidden rounded-md">
                      <img
                        src={photo.url}
                        alt={photo.caption || "Apartment photo"}
                        className="w-full h-full object-cover"
                        data-testid={`img-photo-${photo.id}`}
                      />
                    </div>
                    {photo.isCover && (
                      <Badge
                        variant="default"
                        className="absolute top-2 left-2 gap-1"
                        data-testid={`badge-cover-${photo.id}`}
                      >
                        <Star className="w-3 h-3" /> Cover
                      </Badge>
                    )}
                    <div className="absolute top-2 right-2 flex gap-1 invisible group-hover:visible">
                      {!photo.isCover && (
                        <Button
                          size="icon"
                          variant="secondary"
                          onClick={() => setCoverPhoto.mutate(photo.id)}
                          disabled={setCoverPhoto.isPending}
                          data-testid={`button-set-cover-${photo.id}`}
                        >
                          <Star className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        size="icon"
                        variant="secondary"
                        className="text-destructive"
                        onClick={() => setDeleteApartmentPhotoConfirmId(photo.id)}
                        disabled={deleteApartmentPhoto.isPending}
                        data-testid={`button-delete-photo-img-${photo.id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <ImageIcon className="w-10 h-10 mb-3 opacity-50" />
                <p className="text-sm">No photos uploaded yet.</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  data-testid="button-upload-photos-empty"
                >
                  <Upload className="w-4 h-4 mr-2" /> Upload Photos
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        </>)}
      </div>

      <DeleteConfirmDialog
        open={deleteChecklistConfirm !== null}
        onOpenChange={open => { if (!open) setDeleteChecklistConfirm(null); }}
        title="Delete checklist item?"
        description={deleteChecklistConfirm ? <>This will permanently delete the checklist item <strong>"{deleteChecklistConfirm.textEn}"</strong> from this apartment. Guests will no longer see it on their checkout flow. This cannot be undone.</> : null}
        onConfirm={async () => { if (deleteChecklistConfirm) { await deleteChecklistItem(deleteChecklistConfirm.id); setDeleteChecklistConfirm(null); } }}
      />

      <DeleteConfirmDialog
        open={deletePhotoReqConfirm !== null}
        onOpenChange={open => { if (!open) setDeletePhotoReqConfirm(null); }}
        title="Delete photo requirement?"
        description={deletePhotoReqConfirm ? <>This will permanently delete the photo requirement <strong>"{deletePhotoReqConfirm.labelEn}"</strong>. Guests will no longer be required to upload this photo at checkout. Photos previously submitted by guests will remain in the system. This cannot be undone.</> : null}
        onConfirm={async () => { if (deletePhotoReqConfirm) { await deletePhotoRequirement(deletePhotoReqConfirm.id); setDeletePhotoReqConfirm(null); } }}
      />

      <DeleteConfirmDialog
        open={deleteApartmentPhotoConfirmId !== null}
        onOpenChange={open => { if (!open) setDeleteApartmentPhotoConfirmId(null); }}
        title="Delete this apartment photo?"
        description="This will permanently delete the photo from the apartment gallery and the marketplace listing. This cannot be undone."
        confirmLabel="Delete Photo"
        pending={deleteApartmentPhoto.isPending}
        onConfirm={() => { if (deleteApartmentPhotoConfirmId !== null) { deleteApartmentPhoto.mutate(deleteApartmentPhotoConfirmId); setDeleteApartmentPhotoConfirmId(null); } }}
      />
    </AdminLayout>
  );
}

const zoneLabels: Record<string, string> = {
  kitchen: "Kitchen", bathroom: "Bathroom", bedroom: "Bedroom",
  living_room: "Living Room", dining_room: "Dining Room", balcony: "Balcony",
  laundry: "Laundry", hallway: "Hallway", other: "Other",
};

const conditionConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: any }> = {
  good: { label: "Good", variant: "default", icon: CheckCircle2 },
  damaged: { label: "Damaged", variant: "destructive", icon: AlertTriangle },
  needs_replacement: { label: "Needs Replacement", variant: "destructive", icon: Wrench },
  missing: { label: "Missing", variant: "outline", icon: XCircle },
};

const COST_CATEGORIES = [
  { value: "supplies", label: "Supplies" },
  { value: "maintenance", label: "Maintenance" },
  { value: "utilities", label: "Utilities" },
  { value: "repairs", label: "Repairs" },
  { value: "cleaning_supplies", label: "Cleaning Supplies" },
  { value: "furniture", label: "Furniture" },
  { value: "appliances", label: "Appliances" },
  { value: "insurance", label: "Insurance" },
  { value: "taxes", label: "Taxes & Fees" },
  { value: "marketing", label: "Marketing" },
  { value: "software", label: "Software & Tools" },
  { value: "other", label: "Other" },
];

const RECURRING_OPTIONS = [
  { value: "per_cleaning", label: "Per Cleaning (auto)" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
];

interface AptCostForm {
  category: string;
  description: string;
  amount: string;
  date: string;
  recurring: boolean;
  recurringFrequency: string;
  vendor: string;
  chargedToPm: boolean;
  paidToCleaner: boolean;
  marginPct: string;
}

const emptyCostForm: AptCostForm = {
  category: "", description: "", amount: "",
  date: new Date().toISOString().split("T")[0],
  recurring: false, recurringFrequency: "", vendor: "",
  chargedToPm: false, paidToCleaner: false, marginPct: "",
};

function ApartmentCleaningSuppliesSection({ apartmentId, apartment }: { apartmentId: number; apartment: any }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [showStockView, setShowStockView] = useState(true);

  const { data: catalog = [] } = useQuery<any[]>({ queryKey: ["/api/admin/supply-catalog"] });
  const { data: inventory = [] } = useQuery<any[]>({ queryKey: ["/api/admin/inventory"] });
  const activeItems = catalog.filter((c: any) => c.active);

  const savedOverrides: number[] | null = apartment?.cleaningSupplyOverrides ?? null;
  const [useCustom, setUseCustom] = useState<boolean>(savedOverrides !== null);
  const [selected, setSelected] = useState<Set<number>>(
    savedOverrides !== null ? new Set(savedOverrides) : new Set(activeItems.map((i: any) => i.id))
  );
  const [dirty, setDirty] = useState(false);

  // Sync when apartment data loads
  const initialized = useRef(false);
  useEffect(() => {
    if (!initialized.current && apartment) {
      initialized.current = true;
      const ov = apartment.cleaningSupplyOverrides as number[] | null;
      setUseCustom(ov !== null);
      setSelected(ov !== null ? new Set(ov) : new Set(activeItems.map((i: any) => i.id)));
    }
  }, [apartment, activeItems.length]);

  const saveMutation = useMutation({
    mutationFn: (overrides: number[] | null) =>
      apiRequest("PUT", `/api/admin/apartments/${apartmentId}/cleaning-supplies`, { overrides }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/apartments"] });
      setDirty(false);
      toast({ title: "Cleaning supply override saved" });
    },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  const toggle = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setDirty(true);
  };

  const handleUseCustomToggle = (val: boolean) => {
    setUseCustom(val);
    if (val && selected.size === 0) {
      setSelected(new Set(activeItems.map((i: any) => i.id)));
    }
    setDirty(true);
  };

  const handleSave = () => {
    saveMutation.mutate(useCustom ? Array.from(selected) : null);
  };

  if (activeItems.length === 0) return null;

  // Compute effective supply cost for this apartment
  const bedrooms = apartment?.bedrooms ?? 1;
  const effectiveItems = useCustom
    ? activeItems.filter((i: any) => selected.has(i.id))
    : activeItems;
  const effectiveCost = effectiveItems.reduce((sum: number, item: any) => {
    const qty = bedrooms === 0 ? parseFloat(item.qtyPerJobStudio || "0")
              : bedrooms === 1 ? parseFloat(item.qtyPerJob1br || "0")
              : bedrooms === 2 ? parseFloat(item.qtyPerJob2br || "0")
              : parseFloat(item.qtyPerJob3br || "0");
    return sum + (qty || 0) * (parseFloat(item.costPerUnit || "0") || 0);
  }, 0);

  const categoryLabels: Record<string, string> = {
    cleaning: "Cleaning", toiletries: "Toiletries", kitchen: "Kitchen", laundry: "Laundry", other: "Other",
  };
  const grouped = activeItems.reduce((acc: Record<string, any[]>, item: any) => {
    const cat = item.category || "other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const primaryPmUserId: number | null = apartment?.ownerUserId ?? null;
  const effectiveItemIds = new Set(effectiveItems.map((i: any) => i.id));

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">Cleaning Supplies</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="text-xs text-primary underline-offset-2 hover:underline"
              onClick={() => setShowStockView(v => !v)}
              data-testid="btn-toggle-stock-view"
            >
              {showStockView ? "Hide stock" : "View stock"}
            </button>
            {dirty && (
              <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={handleSave}
                disabled={saveMutation.isPending} data-testid="btn-save-cleaning-supplies">
                {saveMutation.isPending ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : null}
                Save
              </Button>
            )}
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Control which supply catalog items are deducted from inventory when a cleaning job is completed for this apartment.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/40 border">
          <div>
            <p className="text-sm font-medium">Override global catalog</p>
            <p className="text-xs text-muted-foreground">
              {useCustom
                ? `${selected.size} of ${activeItems.length} items selected`
                : "Using all active catalog items (global default)"}
            </p>
          </div>
          <Switch
            checked={useCustom}
            onCheckedChange={handleUseCustomToggle}
            data-testid="switch-custom-supplies"
          />
        </div>
        <div className="flex items-center gap-2 p-2.5 rounded-lg border bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800">
          <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" />
          <span className="text-xs text-green-700 dark:text-green-300">
            Estimated supply cost per cleaning ({bedrooms === 0 ? "Studio" : `${bedrooms}BR`}):
          </span>
          <span className="text-xs font-bold text-green-700 dark:text-green-300 ml-auto">
            ${effectiveCost.toFixed(2)}
          </span>
        </div>

        {showStockView && inventory.length > 0 && (
          <div className="rounded-lg border overflow-hidden">
            <div className="px-3 py-2 bg-muted/30 border-b">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Current Stock Levels</p>
              <p className="text-[11px] text-muted-foreground">Shows org-owned and PM client–owned stock for items used by this apartment</p>
            </div>
            <table className="w-full text-xs" data-testid="table-apt-stock">
              <thead>
                <tr className="border-b bg-muted/10">
                  <th className="py-1.5 px-3 text-left font-medium text-muted-foreground">Item</th>
                  <th className="py-1.5 px-2 text-center font-medium text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Receipt className="h-3 w-3" /> Org</span>
                  </th>
                  {primaryPmUserId !== null && (
                    <th className="py-1.5 px-2 text-center font-medium text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> PM</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {inventory
                  .filter((inv: any) => effectiveItemIds.has(inv.id))
                  .map((inv: any) => {
                    const orgStock = inv.orgStock?.currentStock ?? 0;
                    const orgStatus = inv.orgStock?.status || 'critical';
                    const pmStock = primaryPmUserId !== null
                      ? (inv.pmStocks?.find((p: any) => p.ownerUserId === primaryPmUserId)?.currentStock ?? null)
                      : null;
                    const pmStatus = primaryPmUserId !== null
                      ? (inv.pmStocks?.find((p: any) => p.ownerUserId === primaryPmUserId)?.status || null)
                      : null;
                    return (
                      <tr key={inv.id} className="border-b last:border-0 hover:bg-muted/20">
                        <td className="py-2 px-3 font-medium">{inv.name}</td>
                        <td className="py-2 px-2 text-center">
                          <span className={`font-bold ${orgStatus === 'critical' ? 'text-red-600' : orgStatus === 'low' ? 'text-yellow-600' : 'text-green-600'}`}>
                            {orgStock}
                          </span>
                        </td>
                        {primaryPmUserId !== null && (
                          <td className="py-2 px-2 text-center">
                            {pmStock !== null ? (
                              <span className={`font-bold ${pmStatus === 'critical' ? 'text-red-600' : pmStatus === 'low' ? 'text-yellow-600' : 'text-blue-600'}`}>
                                {pmStock}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}

        {useCustom && (
          <div className="space-y-4">
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  {categoryLabels[cat] || cat}
                </p>
                <div className="space-y-1 border rounded-lg overflow-hidden">
                  {(items as any[]).map((item: any, idx: number) => (
                    <div key={item.id}
                      className={`flex items-center justify-between px-4 py-2.5 hover:bg-muted/30 cursor-pointer ${idx > 0 ? "border-t" : ""}`}
                      onClick={() => toggle(item.id)}
                      data-testid={`supply-toggle-${item.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${selected.has(item.id) ? "" : "text-muted-foreground line-through"}`}>
                          {item.name}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {item.qtyPerJob1br} per 1BR · ${parseFloat(item.costPerUnit || "0").toFixed(2)}/{item.unitOfMeasure}
                        </p>
                      </div>
                      <Switch
                        checked={selected.has(item.id)}
                        onCheckedChange={() => toggle(item.id)}
                        data-testid={`switch-supply-${item.id}`}
                        onClick={e => e.stopPropagation()}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
              <span>{selected.size} items selected</span>
              <button className="text-primary underline-offset-2 hover:underline" onClick={() => { setSelected(new Set(activeItems.map((i: any) => i.id))); setDirty(true); }}>
                Select all
              </button>
              <button className="text-destructive underline-offset-2 hover:underline" onClick={() => { setSelected(new Set()); setDirty(true); }}>
                Clear all
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ApartmentCostsSection({ apartmentId }: { apartmentId: number }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCost, setEditingCost] = useState<any | null>(null);
  const [form, setForm] = useState<AptCostForm>(emptyCostForm);
  const [deleteConfirmCost, setDeleteConfirmCost] = useState<any | null>(null);

  const { data: allCosts = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/admin/unit-costs"],
  });
  const costs = allCosts.filter((c: any) => c.apartmentId === apartmentId);
  const total = costs.reduce((sum: number, c: any) => sum + (parseFloat(c.amount) || 0), 0);

  const createMutation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/admin/unit-costs", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/unit-costs"] });
      setDialogOpen(false);
      setForm(emptyCostForm);
      toast({ title: "Expense added" });
    },
    onError: (e: any) => toast({ title: "Failed to add expense", description: e.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => apiRequest("PUT", `/api/admin/unit-costs/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/unit-costs"] });
      setEditingCost(null);
      setDialogOpen(false);
      setForm(emptyCostForm);
      toast({ title: "Expense updated" });
    },
    onError: (e: any) => toast({ title: "Failed to update", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/admin/unit-costs/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/unit-costs"] });
      toast({ title: "Expense deleted" });
    },
  });

  const openAdd = () => {
    setEditingCost(null);
    setForm(emptyCostForm);
    setDialogOpen(true);
  };

  const openEdit = (cost: any) => {
    setEditingCost(cost);
    setForm({
      category: cost.category,
      description: cost.description || "",
      amount: cost.amount,
      date: cost.date ? new Date(cost.date).toISOString().split("T")[0] : "",
      recurring: cost.recurring || false,
      recurringFrequency: cost.recurringFrequency || "",
      vendor: cost.vendor || "",
      chargedToPm: cost.chargedToPm ?? false,
      paidToCleaner: cost.paidToCleaner ?? false,
      marginPct: cost.marginPct || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    const isPerCleaning = form.recurring && form.recurringFrequency === 'per_cleaning';
    const data = {
      apartmentId,
      category: form.category,
      description: form.description || null,
      amount: form.amount,
      date: isPerCleaning ? null : form.date,
      recurring: form.recurring,
      recurringFrequency: form.recurring ? form.recurringFrequency || null : null,
      vendor: form.vendor || null,
      chargedToPm: form.chargedToPm,
      paidToCleaner: form.paidToCleaner,
      marginPct: form.marginPct || null,
    };
    if (editingCost) {
      updateMutation.mutate({ id: editingCost.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const cleaningMarkerRe = /\s*\[cleaning:(\d+)\]/;
  const perCleaningTemplates = costs.filter((c: any) => c.recurring && c.recurringFrequency === 'per_cleaning');
  const otherCosts = costs.filter((c: any) => !(c.recurring && c.recurringFrequency === 'per_cleaning'));

  const renderCostTable = (rows: any[], showDate = true) => (
    <div className="border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              {showDate && <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>}
              <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">Category</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">Description</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase hidden sm:table-cell">Vendor</th>
              <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground uppercase">Amount</th>
              <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((cost: any) => {
              const catLabel = COST_CATEGORIES.find(c => c.value === cost.category)?.label || cost.category;
              const markerMatch = (cost.description || '').match(cleaningMarkerRe);
              const cleanJobId = markerMatch ? markerMatch[1] : null;
              const displayDesc = (cost.description || '').replace(cleaningMarkerRe, '').trim();
              return (
                <tr key={cost.id} className="hover:bg-muted/30" data-testid={`apt-cost-row-${cost.id}`}>
                  {showDate && (
                    <td className="px-4 py-2.5 text-xs whitespace-nowrap text-muted-foreground">
                      {cost.date ? new Date(cost.date).toLocaleDateString("en-US", { timeZone: "UTC" }) : "—"}
                    </td>
                  )}
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge variant="outline" className="text-xs">{catLabel}</Badge>
                      {cost.recurring && cost.recurringFrequency !== 'per_cleaning' && (
                        <Badge variant="secondary" className="text-[10px]">Recurring</Badge>
                      )}
                      {cleanJobId && (
                        <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800">
                          Job #{cleanJobId}
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-xs max-w-[180px] truncate">{displayDesc || "—"}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground hidden sm:table-cell">{cost.vendor || "—"}</td>
                  <td className="px-4 py-2.5 text-right text-xs font-medium text-red-600 dark:text-red-400">
                    ${parseFloat(cost.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(cost)} data-testid={`btn-edit-apt-cost-${cost.id}`}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmCost(cost)} data-testid={`btn-delete-apt-cost-${cost.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">Costs</CardTitle>
            <Badge variant="secondary" className="ml-1">{costs.length}</Badge>
          </div>
          <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={openAdd} data-testid="btn-add-apt-cost">
            <Plus className="w-4 h-4 mr-1" /> Add Expense
          </Button>
        </CardHeader>
        <CardContent>
          {costs.length > 0 && (
            <div className="flex items-center gap-2 mb-4 p-3 rounded-lg bg-muted/40 border">
              <TrendingDown className="w-4 h-4 text-red-500" />
              <span className="text-sm text-muted-foreground">Total recorded costs:</span>
              <span className="text-sm font-bold text-red-600 dark:text-red-400">
                ${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}

          {isLoading ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Loading costs…</div>
          ) : costs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
                <Receipt className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium">No expenses recorded yet</p>
              <p className="text-xs text-muted-foreground mt-1 mb-4 max-w-xs">
                Track parking, permits, HOA fees, or any other cost specific to this apartment.
              </p>
              <Button size="sm" variant="outline" onClick={openAdd} data-testid="btn-add-apt-cost-empty">
                <Plus className="w-4 h-4 mr-1" /> Add First Expense
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {perCleaningTemplates.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Per-Cleaning Templates</span>
                    <Badge className="text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200">Auto-generated on each completion</Badge>
                  </div>
                  {renderCostTable(perCleaningTemplates, false)}
                </div>
              )}
              {otherCosts.length > 0 && (
                <div>
                  {perCleaningTemplates.length > 0 && (
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Recorded Expenses</p>
                  )}
                  {renderCostTable(otherCosts, true)}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={v => { if (!v) { setDialogOpen(false); setEditingCost(null); setForm(emptyCostForm); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCost ? "Edit Expense" : "Add Expense"}</DialogTitle>
            <DialogDescription>Record a cost specific to this apartment (parking, HOA, permits, etc.)</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <label className="text-sm font-medium mb-1 block">Category *</label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="select-apt-cost-category"><SelectValue placeholder="Select category…" /></SelectTrigger>
                <SelectContent>
                  {COST_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Amount *</label>
              <Input type="number" step="0.01" placeholder="0.00" value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} data-testid="input-apt-cost-amount" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Date *</label>
              <Input type="date" value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))} data-testid="input-apt-cost-date" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Input placeholder="e.g., Monthly parking fee, January" value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="input-apt-cost-description" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Vendor / Payee</label>
              <Input placeholder="e.g., City Parking Authority" value={form.vendor}
                onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))} data-testid="input-apt-cost-vendor" />
            </div>
            <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Billing Options</p>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium">Charge to PM</label>
                  <p className="text-xs text-muted-foreground">Pass this expense to the property manager</p>
                </div>
                <Switch checked={form.chargedToPm} onCheckedChange={v => setForm(f => ({ ...f, chargedToPm: v }))} data-testid="switch-apt-cost-charged-to-pm" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium">Paid to Cleaner</label>
                  <p className="text-xs text-muted-foreground">This expense is paid directly to the cleaner</p>
                </div>
                <Switch checked={form.paidToCleaner} onCheckedChange={v => setForm(f => ({ ...f, paidToCleaner: v }))} data-testid="switch-apt-cost-paid-to-cleaner" />
              </div>
              {form.chargedToPm && (
                <div>
                  <label className="text-sm font-medium mb-1 block">Margin (%)</label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    placeholder="e.g. 15"
                    value={form.marginPct}
                    onChange={e => setForm(f => ({ ...f, marginPct: e.target.value }))}
                    data-testid="input-apt-cost-margin-pct"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {form.marginPct && form.amount
                      ? `PM will be charged $${(parseFloat(form.amount) * (1 + parseFloat(form.marginPct) / 100)).toFixed(2)}`
                      : "Percentage added on top of the amount charged to the PM"}
                  </p>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between pt-1">
              <label className="text-sm font-medium">Recurring Cost</label>
              <Switch checked={form.recurring} onCheckedChange={v => setForm(f => ({ ...f, recurring: v }))} data-testid="switch-apt-cost-recurring" />
            </div>
            {form.recurring && (
              <div>
                <label className="text-sm font-medium mb-1 block">Frequency</label>
                <Select value={form.recurringFrequency} onValueChange={v => setForm(f => ({ ...f, recurringFrequency: v }))}>
                  <SelectTrigger data-testid="select-apt-cost-frequency"><SelectValue placeholder="Select frequency…" /></SelectTrigger>
                  <SelectContent>
                    {RECURRING_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {form.recurringFrequency === 'per_cleaning' && (
                  <p className="text-xs text-muted-foreground mt-1.5 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
                    This is a <strong>template</strong>. Each time a cleaning job is completed for this apartment, a cost entry will be created automatically.
                  </p>
                )}
              </div>
            )}
            <Button className="w-full bg-primary hover:bg-primary/90"
              disabled={!form.category || !form.amount || (form.recurringFrequency !== 'per_cleaning' && !form.date) || createMutation.isPending || updateMutation.isPending}
              onClick={handleSubmit}
              data-testid="btn-submit-apt-cost"
            >
              {(createMutation.isPending || updateMutation.isPending) && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              {editingCost ? "Save Changes" : "Add Expense"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteConfirmCost !== null}
        onOpenChange={open => { if (!open) setDeleteConfirmCost(null); }}
        title="Delete this expense?"
        description={deleteConfirmCost ? <>This will permanently delete the <strong>${parseFloat(deleteConfirmCost.amount || "0").toFixed(2)}</strong> expense{deleteConfirmCost.description ? <> ({deleteConfirmCost.description})</> : null} from this apartment's cost history. This cannot be undone.</> : null}
        confirmLabel="Delete Expense"
        pending={deleteMutation.isPending}
        onConfirm={() => { if (deleteConfirmCost) { deleteMutation.mutate(deleteConfirmCost.id); setDeleteConfirmCost(null); } }}
      />
    </>
  );
}

function ApartmentInventorySection({ apartmentId }: { apartmentId: number }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [editForm, setEditForm] = useState({ quantity: "1", condition: "good", zone: "other", notes: "" });
  const [deleteConfirmInvItem, setDeleteConfirmInvItem] = useState<any | null>(null);

  const { data: inventoryItems = [] } = useQuery<any[]>({
    queryKey: ['/api/admin/apartments', apartmentId, 'inventory'],
    enabled: !!apartmentId,
  });

  const { data: templates = [] } = useQuery<any[]>({
    queryKey: ['/api/admin/inventory-templates'],
  });

  const { data: reports = [] } = useQuery<any[]>({
    queryKey: ['/api/admin/inventory-reports'],
    select: (data: any[]) => data.filter(r => r.apartmentId === apartmentId),
  });

  const templatesMap = new Map((templates || []).map((t: any) => [t.id, t]));

  const addItem = useMutation({
    mutationFn: (data: any) => apiRequest("POST", `/api/admin/apartments/${apartmentId}/inventory`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/apartments', apartmentId, 'inventory'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/apartment-inventory'] });
      setAddDialogOpen(false);
      toast({ title: "Item added" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const updateItem = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => apiRequest("PUT", `/api/admin/apartments/${apartmentId}/inventory/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/apartments', apartmentId, 'inventory'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/apartment-inventory'] });
      setEditingItem(null);
      toast({ title: "Item updated" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteItem = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/admin/apartments/${apartmentId}/inventory/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/apartments', apartmentId, 'inventory'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/apartment-inventory'] });
      toast({ title: "Item removed" });
    },
  });

  const groupedByZone = (inventoryItems || []).reduce((acc: Record<string, any[]>, item: any) => {
    const zone = item.zone || "other";
    if (!acc[zone]) acc[zone] = [];
    acc[zone].push(item);
    return acc;
  }, {});

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">Inventory</CardTitle>
            <Badge variant="secondary" className="ml-1">{inventoryItems.length}</Badge>
          </div>
          <Button size="sm" onClick={() => setAddDialogOpen(true)} data-testid="button-add-inventory">
            <Plus className="w-4 h-4 mr-2" /> Add Item
          </Button>
        </CardHeader>
        <CardContent>
          {inventoryItems.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No inventory items yet. Add items from templates.</p>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedByZone).map(([zone, items]) => (
                <div key={zone}>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{zoneLabels[zone] || zone}</h4>
                  <div className="space-y-1">
                    {(items as any[]).map((item: any) => {
                      const template = templatesMap.get(item.templateId);
                      const cConfig = conditionConfig[item.condition] || conditionConfig.good;
                      const CondIcon = cConfig.icon;
                      return (
                        <div key={item.id} className="group flex items-center justify-between gap-3 py-2 px-2 rounded hover:bg-muted/50" data-testid={`apt-inventory-item-${item.id}`}>
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="text-sm font-medium">{template?.name || "Unknown"}</span>
                            <Badge variant="outline" className="text-[10px]">x{item.quantity}</Badge>
                            <Badge variant={cConfig.variant} className="text-[10px]">
                              <CondIcon className="w-3 h-3 mr-1" />
                              {cConfig.label}
                            </Badge>
                            {item.notes && <span className="text-xs text-muted-foreground truncate">{item.notes}</span>}
                          </div>
                          <div className="flex items-center gap-1 shrink-0 invisible group-hover:visible">
                            <Button variant="ghost" size="sm" onClick={() => {
                              setEditingItem(item);
                              setEditForm({
                                quantity: String(item.quantity),
                                condition: item.condition,
                                zone: item.zone,
                                notes: item.notes || "",
                              });
                            }} data-testid={`button-edit-inv-${item.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmInvItem(item)} data-testid={`button-delete-inv-${item.id}`}>
                              <Trash2 className="w-3.5 h-3.5 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {reports.length > 0 && (
            <div className="mt-6 pt-4 border-t">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Recent Reports</h4>
              <div className="space-y-2">
                {reports.map((r: any) => {
                  const flagged = (r.items || []).filter((i: any) => i.condition !== "good");
                  return (
                    <div key={r.id} className="text-xs flex items-center gap-2" data-testid={`apt-report-${r.id}`}>
                      <span className="text-muted-foreground">{r.createdAt ? new Date(r.createdAt).toLocaleDateString("en-US", { timeZone: "UTC" }) : ""}</span>
                      <Badge variant="secondary" className="text-[10px]">{(r.items || []).length} checked</Badge>
                      {flagged.length > 0 && <Badge variant="destructive" className="text-[10px]">{flagged.length} flagged</Badge>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Inventory Item</DialogTitle>
            <DialogDescription>Select a template to add to this apartment.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {templates.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No templates defined. Go to Inventory page to create templates first.</p>
            ) : (
              templates.map((t: any) => (
                <Button
                  key={t.id}
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => addItem.mutate({ templateId: t.id, quantity: t.defaultQuantity, condition: "good", zone: t.zone, cost: t.unitCost || null })}
                  data-testid={`button-add-template-${t.id}`}
                >
                  <Package className="w-4 h-4 mr-2" />
                  {t.name}
                  <Badge variant="outline" className="ml-auto text-[10px]">{zoneLabels[t.zone] || t.zone}</Badge>
                </Button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingItem} onOpenChange={(open) => !open && setEditingItem(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Inventory Item</DialogTitle>
            <DialogDescription>Update quantity, condition, or notes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Quantity</Label>
              <Input
                type="number"
                min="0"
                value={editForm.quantity}
                onChange={e => setEditForm({ ...editForm, quantity: e.target.value })}
                data-testid="input-edit-inv-quantity"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Condition</Label>
              <Select value={editForm.condition} onValueChange={v => setEditForm({ ...editForm, condition: v })}>
                <SelectTrigger data-testid="select-edit-inv-condition">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INVENTORY_CONDITIONS.map(c => (
                    <SelectItem key={c} value={c}>{conditionConfig[c]?.label || c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Zone</Label>
              <Select value={editForm.zone} onValueChange={v => setEditForm({ ...editForm, zone: v })}>
                <SelectTrigger data-testid="select-edit-inv-zone">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INVENTORY_ZONES.map(z => (
                    <SelectItem key={z} value={z}>{zoneLabels[z] || z}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Notes</Label>
              <Input
                value={editForm.notes}
                onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder="Optional notes"
                data-testid="input-edit-inv-notes"
              />
            </div>
            <Button
              className="w-full"
              onClick={() => editingItem && updateItem.mutate({
                id: editingItem.id,
                data: { quantity: parseInt(editForm.quantity), condition: editForm.condition, zone: editForm.zone, notes: editForm.notes || null },
              })}
              data-testid="button-save-inv-edit"
            >
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteConfirmInvItem !== null}
        onOpenChange={open => { if (!open) setDeleteConfirmInvItem(null); }}
        title="Delete this inventory item?"
        description={deleteConfirmInvItem ? <>This will permanently delete the inventory item{deleteConfirmInvItem.name ? <> <strong>"{deleteConfirmInvItem.name}"</strong></> : null} from this apartment. Historical inventory reports referencing this item will remain. This cannot be undone.</> : null}
        confirmLabel="Delete Item"
        pending={deleteItem.isPending}
        onConfirm={() => { if (deleteConfirmInvItem) { deleteItem.mutate(deleteConfirmInvItem.id); setDeleteConfirmInvItem(null); } }}
      />
    </>
  );
}
